import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
    Background,
    Controls,
    addEdge,
    applyNodeChanges,
    applyEdgeChanges,
    Handle,
    Position,
    MiniMap,
    Edge,
    Node,
    useReactFlow,
    ReactFlowProvider,
    useViewport,
    useStore,
    Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { flowService } from '../services/flowService';
import { QuestionForm, ConclusionForm } from '../components/QuestionEditor';
import api from '../services/api';

import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    FormControlLabel,
    Switch,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    CircularProgress,
    Grid,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Drawer,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    Divider,
    Card,
    CardContent,
    InputAdornment,
    Fab,
    Slider,
} from '@mui/material';
import {
    Save as SaveIcon,
    Preview as PreviewIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as SuccessIcon,
    Cancel as FailIcon,
    Info as InfoIcon,
    ContentCopy as ContentCopyIcon,
    Flag as FlagIcon,
    Close as CloseIcon,
    AddCircle as AddCircleIcon,
    Check as CheckIcon,
    Clear as ClearIcon,
    PlayArrow as PlayArrowIcon,
    ExpandMore as ExpandMoreIcon,
    QuestionAnswer as QuestionAnswerIcon,
    Assignment as AssignmentIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Help as HelpIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    Search as SearchIcon,
    Remove as RemoveIcon,
    CropFree as CropFreeIcon,
} from '@mui/icons-material';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';

import { useNotification } from '../contexts/NotificationContext';
import { questionBankService } from '../services/questionBankService';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Componente NodeStatusIndicator
const NodeStatusIndicator = ({ status, children }) => {
    const theme = useTheme();
    
    const getStatusColor = () => {
        switch (status) {
            case 'success':
                return '#22c55e'; // Verde mais vibrante
            case 'error':
                return '#ef4444'; // Vermelho mais vibrante
            case 'loading':
                return '#3b82f6'; // Azul mais vibrante
            default:
                return 'transparent';
        }
    };
    
    return (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
            {status && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        border: '2px solid',
                        borderColor: getStatusColor(),
                        borderRadius: '16px',
                        animation: status === 'loading' ? 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
                        '@keyframes pulse': {
                            '0%, 100%': {
                                opacity: 1,
                            },
                            '50%': {
                                opacity: 0.3,
                            },
                        },
                        zIndex: 1,
                        pointerEvents: 'none',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -1,
                            left: -1,
                            right: -1,
                            bottom: -1,
                            borderRadius: '17px',
                            background: `linear-gradient(to right, ${getStatusColor()}, ${getStatusColor()})`,
                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                            opacity: status === 'loading' ? 0.5 : 0.2
                        }
                    }}
                />
            )}
            {children}
        </Box>
    );
};

// Componente personalizado para nós de questão
const QuestionNode = ({ data, selected }) => {
    const theme = useTheme();
    
    // Determina a cor de fundo com base no tipo de conclusão
    const getBackgroundColor = () => {
        if (!data.is_final) {
            return theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.9)
                : theme.palette.background.paper;
        }
        
        // Se for uma conclusão, verifica o tipo
        if (data.is_success === true) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.success.main, 0.2)
                : alpha(theme.palette.success.main, 0.1);
        } else if (data.is_success === false) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.error.main, 0.2)
                : alpha(theme.palette.error.main, 0.1);
        } else {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.2)
                : alpha(theme.palette.info.main, 0.1);
        }
    };
    
    // Determina a cor da borda com base no tipo de conclusão
    const getBorderColor = () => {
        if (!data.is_final) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.divider, 0.7)
                : theme.palette.divider;
        }
        
        // Se for uma conclusão, verifica o tipo
        if (data.is_success === true) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.success.main, 0.5)
                : alpha(theme.palette.success.main, 0.3);
        } else if (data.is_success === false) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.error.main, 0.5)
                : alpha(theme.palette.error.main, 0.3);
        } else {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.5)
                : alpha(theme.palette.info.main, 0.3);
        }
    };
    
    // Determina a cor do texto do tipo de nó
    const getTypeColor = () => {
        if (!data.is_final) {
            return theme.palette.text.secondary;
        }
        
        if (data.is_success === true) {
            return theme.palette.success.main;
        } else if (data.is_success === false) {
            return theme.palette.error.main;
        } else {
            return theme.palette.info.main;
        }
    };
    
    // Determina o ícone do tipo de conclusão
    const getTypeIcon = () => {
        if (!data.is_final) {
            return null;
        }
        
        if (data.is_success === true) {
            return <CheckCircleIcon fontSize="small" sx={{ ml: 1 }} />;
        } else if (data.is_success === false) {
            return <CancelIcon fontSize="small" sx={{ ml: 1 }} />;
        }
        
        return null;
    };
    
    // Determina o status do nó
    const getNodeStatus = () => {
        if (!selected) return null;
        if (data.is_final) {
            return data.is_success ? 'success' : 'error';
        }
        return 'loading';
    };
    
    return (
        <Box sx={{ position: 'relative' }}>
            {/* Indicador de primeira questão */}
            {!data.is_final && data.isFirstQuestion && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        zIndex: 1,
                        boxShadow: theme.shadows[2],
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    <PlayCircleOutlineIcon sx={{ fontSize: 14 }} />
                    Primeira Questão
                </Box>
            )}
            <NodeStatusIndicator status={getNodeStatus()}>
                <Paper
                    sx={{
                        minWidth: 300,
                        maxWidth: 400,
                        backgroundColor: getBackgroundColor(),
                        border: '1px solid',
                        borderColor: getBorderColor(),
                        borderRadius: 2,
                        position: 'relative',
                        boxShadow: theme.shadows[2],
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: theme.shadows[4]
                        },
                        zIndex: 0,
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        {/* Handle de entrada no topo */}
                        <Handle
                            type="target"
                            position={Position.Top}
                            style={{ 
                                background: theme.palette.primary.main,
                                width: 12,
                                height: 12,
                                top: -6,
                                zIndex: 999999,
                                border: `2px solid ${theme.palette.background.paper}`,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        />

                        {/* Cabeçalho com tipo e referência */}
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 2,
                            gap: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                    variant="caption" 
                                    component="div"
                                    sx={{ 
                                        color: getTypeColor(),
                                        fontWeight: 600,
                                        px: 1.5,
                                        py: 0.5,
                                        backgroundColor: alpha(getTypeColor(), 0.1),
                                        borderRadius: 2,
                                        fontSize: '0.95rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        boxShadow: `0 0 0 1px ${alpha(getTypeColor(), 0.2)}`
                                    }}
                                >
                                    {data.is_final ? 'Conclusão' : 'Pergunta'}
                                    {getTypeIcon()}
                                </Typography>
                                
                                <Typography 
                                    variant="caption" 
                                    component="div"
                                    sx={{ 
                                        color: theme.palette.text.secondary,
                                        fontWeight: 600,
                                        px: 1.5,
                                        py: 0.5,
                                        backgroundColor: alpha(theme.palette.background.default, 0.7),
                                        borderRadius: 2,
                                        fontSize: '0.95rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        boxShadow: `0 0 0 1px ${alpha(theme.palette.divider, 0.3)}`
                                    }}
                                >
                                    ID: {data.id}
                                </Typography>
                                
                                {/* Removendo o botão de definir primeira questão */}
                            </Box>
                            {data.article_reference && (
                                <Typography 
                                    variant="caption" 
                                    component="div"
                                    sx={{ 
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.95rem',
                                        px: 1.5,
                                        py: 0.5,
                                        backgroundColor: alpha(theme.palette.background.default, 0.7),
                                        borderRadius: 2,
                                        maxWidth: '60%',
                                        boxShadow: `0 0 0 1px ${alpha(theme.palette.divider, 0.3)}`,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    Ref: {data.article_reference}
                                </Typography>
                            )}
                        </Box>

                        {/* Texto principal */}
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                fontWeight: 500,
                                color: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.95)' 
                                    : theme.palette.text.primary,
                                mb: 3,
                                lineHeight: 1.5,
                                fontSize: '1.1rem',
                                textShadow: theme.palette.mode === 'dark' 
                                    ? '0px 1px 2px rgba(0, 0, 0, 0.5)' 
                                    : 'none'
                            }}
                        >
                            {data.text}
                        </Typography>
                        
                        {/* Opções e edges */}
                        {!data.is_final && data.options && (
                            <Box sx={{ mt: 2 }}>
                                <Box 
                                    sx={{ 
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        position: 'relative',
                                        zIndex: 0
                                    }}
                                >
                                    {(() => {
                                        // Garante que options seja um array
                                        let options = data.options;
                                        if (typeof options === 'string') {
                                            try {
                                                options = JSON.parse(options);
                                            } catch (e) {
                                                console.error('Erro ao parsear options:', e);
                                                options = [];
                                            }
                                        }
                                        // Se não for um array, converte para array vazio
                                        if (!Array.isArray(options)) {
                                            console.warn('options não é um array:', options);
                                            options = [];
                                        }
                                        
                                        return options.map((option, index) => (
                                            <Box 
                                                key={index} 
                                                sx={{ 
                                                    position: 'relative',
                                                    pb: 3,
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Paper
                                                    sx={{
                                                        p: 1.5,
                                                        width: '100%',
                                                        textAlign: 'center',
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                        border: '1px solid',
                                                        borderColor: alpha(theme.palette.primary.main, 0.2),
                                                        borderRadius: 2,
                                                        position: 'relative',
                                                        transition: 'all 0.2s ease',
                                                        boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                                                        },
                                                        zIndex: 0
                                                    }}
                                                >
                                                    <Typography 
                                                        variant="body2"
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 600,
                                                            fontSize: '1rem'
                                                        }}
                                                    >
                                                        {option}
                                                    </Typography>
                                                </Paper>
                                                
                                                {/* Handle de saída para cada opção */}
                                                <Handle
                                                    type="source"
                                                    position={Position.Bottom}
                                                    id={index.toString()}
                                                    style={{
                                                        background: theme.palette.primary.main,
                                                        width: 12,
                                                        height: 12,
                                                        bottom: 0,
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        zIndex: 999999,
                                                        border: `2px solid ${theme.palette.background.paper}`,
                                                        borderRadius: '50%',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                />
                                            </Box>
                                        ));
                                    })()}
                                </Box>
                            </Box>
                        )}

                        {/* Mensagem final para conclusões */}
                        {data.is_final && data.final_message && (
                            <Box sx={{ mt: 2 }}>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                        fontStyle: 'italic',
                                        backgroundColor: alpha(
                                            data.is_success === true 
                                                ? theme.palette.success.main 
                                                : data.is_success === false 
                                                    ? theme.palette.error.main 
                                                    : theme.palette.info.main, 
                                            0.08
                                        ),
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: alpha(
                                            data.is_success === true 
                                                ? theme.palette.success.main 
                                                : data.is_success === false 
                                                    ? theme.palette.error.main 
                                                    : theme.palette.info.main, 
                                            0.2
                                        ),
                                        boxShadow: `0 2px 4px ${alpha(
                                            data.is_success === true 
                                                ? theme.palette.success.main 
                                                : data.is_success === false 
                                                    ? theme.palette.error.main 
                                                    : theme.palette.info.main,
                                            0.1
                                        )}`,
                                        lineHeight: 1.6,
                                        fontSize: '1.05rem'
                                    }}
                                >
                                    {data.final_message}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </NodeStatusIndicator>
        </Box>
    );
};

// Tipos de nós
const nodeTypes = {
    question: QuestionNode,
};

// Componente do menu lateral interno
const SidePanel = ({ 
    isMenuExpanded,
    handleToggleExpand,
    expandedSections,
    handleSectionToggle,
    includedQuestions,
    availableQuestions,
    conclusions,
    renderQuestionCard,
    theme 
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Filtragem baseada no termo de busca
    const filterItems = (items) => {
        if (!searchTerm.trim()) return items;
        
        return items.filter(item => {
            const searchLower = searchTerm.toLowerCase();
            const textMatch = item.text && item.text.toLowerCase().includes(searchLower);
            const finalMessageMatch = item.final_message && item.final_message.toLowerCase().includes(searchLower);
            const referenceMatch = item.article_reference && item.article_reference.toLowerCase().includes(searchLower);
            const optionsMatch = Array.isArray(item.options) && 
                item.options.some(opt => opt.toLowerCase().includes(searchLower));
            
            return textMatch || finalMessageMatch || referenceMatch || optionsMatch;
        });
    };

    // Separar conclusões por tipo
    const positiveConclusions = conclusions.filter(c => c.is_success);
    const negativeConclusions = conclusions.filter(c => !c.is_success);
    
    // Separar questões e conclusões incluídas
    const includedPositiveConclusions = includedQuestions.filter(q => q.is_final && q.is_success);
    const includedNegativeConclusions = includedQuestions.filter(q => q.is_final && !q.is_success);
    const includedQuestionsOnly = includedQuestions.filter(q => !q.is_final);
    
    // Aplicar filtro de busca
    const filteredIncludedQuestions = filterItems(includedQuestionsOnly);
    const filteredIncludedPositiveConclusions = filterItems(includedPositiveConclusions);
    const filteredIncludedNegativeConclusions = filterItems(includedNegativeConclusions);
    const filteredAvailableQuestions = filterItems(availableQuestions);
    const filteredPositiveConclusions = filterItems(positiveConclusions);
    const filteredNegativeConclusions = filterItems(negativeConclusions);

    // Função para renderizar o cabeçalho de uma seção
    const renderSectionHeader = (title, count, section, color) => {
        return (
            <Box
                sx={{
                    borderRadius: '8px 8px 0 0',
                    overflow: 'hidden',
                    mb: expandedSections[section] ? 0 : 1,
                    boxShadow: expandedSections[section] ? 'none' : `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
                }}
            >
                <Box
                    onClick={() => handleSectionToggle(section)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        cursor: 'pointer',
                        backgroundColor: alpha(color, 0.12),
                        borderRadius: expandedSections[section] ? '8px 8px 0 0' : '8px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: alpha(color, 0.18),
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: color,
                                boxShadow: `0 0 0 2px ${alpha(color, 0.3)}`,
                            }}
                        />
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {title}
                        </Typography>
                        <Chip
                            label={count}
                            size="small"
                            sx={{
                                height: 20,
                                minWidth: 28,
                                backgroundColor: alpha(color, 0.2),
                                color: color,
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                '& .MuiChip-label': {
                                    px: 1,
                                }
                            }}
                        />
                    </Box>
                    <IconButton
                        size="small"
                        sx={{
                            p: 0.5,
                            color: alpha(theme.palette.text.primary, 0.7),
                            transform: expandedSections[section] ? 'rotate(180deg)' : 'rotate(0)',
                            transition: 'transform 0.3s ease',
                        }}
                    >
                        <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                width: isMenuExpanded ? 320 : 0,
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                backdropFilter: 'blur(12px)',
                transition: 'width 0.3s ease',
                borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: `2px 0 20px ${alpha(theme.palette.common.black, 0.03)}`,
            }}
        >
            {/* Conteúdo do menu */}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: isMenuExpanded ? 'flex' : 'none',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Cabeçalho do menu */}
                <Box 
                    sx={{ 
                        p: 2.5,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        backgroundColor: alpha(theme.palette.background.paper, 0.4),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 4px 30px ${alpha(theme.palette.common.black, 0.03)}`,
                    }}
                >
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 600, 
                            fontSize: '1.1rem',
                            mb: 2,
                            color: theme.palette.text.primary,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Banco de Questões
                    </Typography>
                    
                    {/* Barra de busca */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar questões e conclusões..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => setSearchTerm('')}
                                        edge="end"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                            sx: {
                                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(8px)',
                                borderRadius: '12px',
                                '& fieldset': {
                                    borderColor: 'transparent',
                                    transition: 'border-color 0.2s ease'
                                },
                                '&:hover fieldset': {
                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main,
                                }
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                                },
                                '&.Mui-focused': {
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                                }
                            }
                        }}
                    />
                </Box>

                {/* Lista de questões com scroll */}
                <Box sx={{ 
                    overflowY: 'auto',
                    height: '100%',
                    p: 2,
                    '&::-webkit-scrollbar': {
                        width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.3),
                        },
                    },
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* SEÇÃO: ITENS INCLUÍDOS */}
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 700, 
                                color: alpha(theme.palette.text.primary, 0.7),
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px',
                                mb: 1,
                                mt: 1,
                            }}
                        >
                            Itens Incluídos no Fluxo
                        </Typography>
                        
                        {/* Questões Incluídas */}
                        {renderSectionHeader('Questões', filteredIncludedQuestions.length, 'includedQuestions', theme.palette.primary.main)}
                        <Box
                            sx={{
                                display: expandedSections.includedQuestions ? 'block' : 'none',
                                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: '0 0 8px 8px',
                                mb: 2,
                                boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                borderTop: 'none',
                            }}
                        >
                            <Box sx={{ p: 1 }}>
                                {filteredIncludedQuestions.map(q => renderQuestionCard(q, true))}
                                {filteredIncludedQuestions.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        {searchTerm ? 'Nenhuma questão encontrada' : 'Nenhuma questão incluída'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Conclusões Positivas Incluídas */}
                        {renderSectionHeader('Conclusões Positivas', filteredIncludedPositiveConclusions.length, 'includedPositive', '#4caf50')}
                        <Box
                            sx={{
                                display: expandedSections.includedPositive ? 'block' : 'none',
                                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: '0 0 8px 8px',
                                mb: 2,
                                boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
                                border: `1px solid ${alpha('#4caf50', 0.1)}`,
                                borderTop: 'none',
                            }}
                        >
                            <Box sx={{ p: 1 }}>
                                {filteredIncludedPositiveConclusions.map(q => renderQuestionCard(q, true))}
                                {filteredIncludedPositiveConclusions.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        {searchTerm ? 'Nenhuma conclusão positiva encontrada' : 'Nenhuma conclusão positiva incluída'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Conclusões Negativas Incluídas */}
                        {renderSectionHeader('Conclusões Negativas', filteredIncludedNegativeConclusions.length, 'includedNegative', '#f44336')}
                        <Box
                            sx={{
                                display: expandedSections.includedNegative ? 'block' : 'none',
                                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: '0 0 8px 8px',
                                mb: 2,
                                boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
                                border: `1px solid ${alpha('#f44336', 0.1)}`,
                                borderTop: 'none',
                            }}
                        >
                            <Box sx={{ p: 1 }}>
                                {filteredIncludedNegativeConclusions.map(q => renderQuestionCard(q, true))}
                                {filteredIncludedNegativeConclusions.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        {searchTerm ? 'Nenhuma conclusão negativa encontrada' : 'Nenhuma conclusão negativa incluída'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* SEÇÃO: ITENS DISPONÍVEIS */}
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 700, 
                                color: alpha(theme.palette.text.primary, 0.7),
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px',
                                mb: 1,
                                mt: 2,
                            }}
                        >
                            Itens Disponíveis para Uso
                        </Typography>
                        
                        {/* Questões Disponíveis */}
                        {renderSectionHeader('Questões', filteredAvailableQuestions.length, 'availableQuestions', theme.palette.primary.main)}
                        <Box
                            sx={{
                                display: expandedSections.availableQuestions ? 'block' : 'none',
                                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: '0 0 8px 8px',
                                mb: 2,
                                boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                borderTop: 'none',
                            }}
                        >
                            <Box sx={{ p: 1 }}>
                                {filteredAvailableQuestions.map(q => renderQuestionCard(q))}
                                {filteredAvailableQuestions.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        {searchTerm ? 'Nenhuma questão encontrada' : 'Nenhuma questão disponível'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Conclusões Positivas Disponíveis */}
                        {renderSectionHeader('Conclusões Positivas', filteredPositiveConclusions.length, 'availablePositive', '#4caf50')}
                        <Box
                            sx={{
                                display: expandedSections.availablePositive ? 'block' : 'none',
                                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: '0 0 8px 8px',
                                mb: 2,
                                boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
                                border: `1px solid ${alpha('#4caf50', 0.1)}`,
                                borderTop: 'none',
                            }}
                        >
                            <Box sx={{ p: 1 }}>
                                {filteredPositiveConclusions.map(q => renderQuestionCard(q))}
                                {filteredPositiveConclusions.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        {searchTerm ? 'Nenhuma conclusão positiva encontrada' : 'Nenhuma conclusão positiva disponível'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Conclusões Negativas Disponíveis */}
                        {renderSectionHeader('Conclusões Negativas', filteredNegativeConclusions.length, 'availableNegative', '#f44336')}
                        <Box
                            sx={{
                                display: expandedSections.availableNegative ? 'block' : 'none',
                                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: '0 0 8px 8px',
                                mb: 2,
                                boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
                                border: `1px solid ${alpha('#f44336', 0.1)}`,
                                borderTop: 'none',
                            }}
                        >
                            <Box sx={{ p: 1 }}>
                                {filteredNegativeConclusions.map(q => renderQuestionCard(q))}
                                {filteredNegativeConclusions.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        {searchTerm ? 'Nenhuma conclusão negativa encontrada' : 'Nenhuma conclusão negativa disponível'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// Componente ZoomSlider
const ZoomSlider = () => {
    const theme = useTheme();
    const { zoom } = useViewport();
    const { zoomTo, zoomIn, zoomOut, fitView } = useReactFlow();
    const { minZoom, maxZoom } = useStore((state) => ({
        minZoom: state.minZoom,
        maxZoom: state.maxZoom,
    }));

    return (
        <Panel position="bottom-center" style={{ marginBottom: 20 }}>
            <Paper
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    backdropFilter: 'blur(8px)',
                    boxShadow: theme.shadows[2],
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transform: 'translateX(-50%)',  // Centraliza horizontalmente
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => zoomOut({ duration: 300 })}
                    sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                    }}
                >
                    <RemoveIcon fontSize="small" />
                </IconButton>

                <Slider
                    value={zoom}
                    min={minZoom}
                    max={maxZoom}
                    step={0.01}
                    onChange={(_, value) => zoomTo(value, { duration: 300 })}
                    sx={{
                        width: 120,
                        '& .MuiSlider-rail': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        },
                        '& .MuiSlider-track': {
                            backgroundColor: theme.palette.primary.main,
                        },
                        '& .MuiSlider-thumb': {
                            backgroundColor: theme.palette.primary.main,
                            '&:hover, &.Mui-focusVisible': {
                                boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                            },
                        },
                    }}
                />

                <IconButton
                    size="small"
                    onClick={() => zoomIn({ duration: 300 })}
                    sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                    }}
                >
                    <AddIcon fontSize="small" />
                </IconButton>

                <Button
                    size="small"
                    onClick={() => zoomTo(1, { duration: 300 })}
                    sx={{
                        minWidth: 'auto',
                        px: 1,
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                    }}
                >
                    {Math.round(zoom * 100)}%
                </Button>

                <IconButton
                    size="small"
                    onClick={() => fitView({ duration: 300 })}
                    sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                    }}
                >
                    <CropFreeIcon fontSize="small" />
                </IconButton>
            </Paper>
        </Panel>
    );
};

// Componente interno que usa o hook useReactFlow
const FlowEditor = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const { showNotification } = useNotification();
    const reactFlowInstance = useRef(null);
    
    // Estados
    const [flow, setFlow] = useState({ name: 'Novo Fluxo', description: '', available: true });
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState('');
    const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
    const [questionBankItems, setQuestionBankItems] = useState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [sideMenuOpen, setSideMenuOpen] = useState(true);
    const [draggedPosition, setDraggedPosition] = useState(null);
    const [isMenuExpanded, setIsMenuExpanded] = useState(true);
    const [expandedSections, setExpandedSections] = useState({
        includedQuestions: true,
        includedPositive: true,
        includedNegative: true,
        availableQuestions: true,
        availablePositive: false,
        availableNegative: false
    });
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [createModalType, setCreateModalType] = useState('question');
    const [speedDialOpen, setSpeedDialOpen] = useState(false);
    const [lastViewport, setLastViewport] = useState(null);
    const [preservingViewport, setPreservingViewport] = useState(false);
    const [multiSelectedNodes, setMultiSelectedNodes] = useState([]);
    const [isQuestionFormOpen, setIsQuestionFormOpen] = useState(false);
    const [isConclusionFormOpen, setIsConclusionFormOpen] = useState(false); // Novo estado para o modal de edição de conclusão
    const [selectedNodeForEdit, setSelectedNodeForEdit] = useState(null);
    
    /**
     * Sistema de Preservação de Viewport
     * 
     * Este sistema garante que o viewport (posição e zoom) seja mantido durante operações
     * que modificam o fluxo, como adicionar, remover, clonar ou salvar nós.
     * 
     * Componentes principais:
     * 1. preserveCurrentViewport: Salva o viewport atual antes de uma operação
     * 2. setPreservingViewport: Controla se o viewport deve ser preservado
     * 3. restoreViewport: Restaura o viewport após uma operação
     * 4. useEffect: Monitora mudanças nos nós/arestas e restaura o viewport quando necessário
     * 
     * Fluxo de uso:
     * 1. Antes de uma operação que modifica o fluxo, chamar preserveCurrentViewport()
     * 2. Realizar a operação (ex: adicionar nó, remover nó, etc.)
     * 3. O useEffect detecta a mudança nos nós/arestas e chama restoreViewport()
     * 4. Em caso de erro ou cancelamento, chamar setPreservingViewport(false)
     * 
     * Nota sobre adição de nós:
     * Para evitar recarregar o fluxo inteiro ao adicionar um nó (o que causaria uma atualização
     * desnecessária da tela), adicionamos o nó diretamente ao estado em vez de chamar fetchFlow().
     * Isso mantém o viewport e proporciona uma experiência mais fluida.
     */
    
    // Funções do menu lateral
    const handleSectionToggle = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleToggleExpand = () => {
        setIsMenuExpanded(!isMenuExpanded);
    };

    const renderQuestionCard = (question, isIncluded = false) => {
        if (question.is_final) {
            const backgroundColor = question.is_success 
                ? alpha('#e8f5e9', 0.7)  // Verde mais suave e translúcido
                : alpha('#ffebee', 0.7); // Vermelho mais suave e translúcido
            
            return (
                <Paper
                    sx={{
                        mb: 0.5,
                        cursor: 'grab',
                        backgroundColor: backgroundColor,
                        overflow: 'visible',
                        border: '1px solid',
                        borderColor: question.is_success 
                            ? alpha('#4caf50', 0.2)
                            : alpha('#f44336', 0.2),
                        position: 'relative',
                        '&:hover': {
                            backgroundColor: question.is_success 
                                ? alpha('#e8f5e9', 0.9)
                                : alpha('#ffebee', 0.9),
                        }
                    }}
                    onDragStart={(e) => handleDragStart(e, question)}
                    draggable
                    elevation={0}
                >
                    <Box sx={{ p: 1.5 }}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap'
                            }}>
                                <Chip
                                    label={question.is_success ? "Conclusão" : "Conclusão negativa"}
                                    icon={question.is_success 
                                        ? <CheckCircleIcon sx={{ fontSize: '16px !important' }} />
                                        : <CancelIcon sx={{ fontSize: '16px !important' }} />
                                    }
                                    sx={{
                                        backgroundColor: question.is_success ? '#e8f5e9' : '#ffebee',
                                        color: question.is_success ? '#2e7d32' : '#d32f2f',
                                        borderRadius: '12px',
                                        '& .MuiChip-icon': {
                                            color: question.is_success ? '#2e7d32' : '#d32f2f',
                                        },
                                        border: '1px solid',
                                        borderColor: question.is_success 
                                            ? alpha('#4caf50', 0.3)
                                            : alpha('#f44336', 0.3),
                                        height: '24px',
                                        fontSize: '0.75rem',
                                    }}
                                />
                                <Chip
                                    label={`ID: ${question.id}`}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                        borderRadius: '12px',
                                        height: '24px',
                                        fontSize: '0.75rem',
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.2),
                                        color: theme.palette.text.secondary,
                                    }}
                                />
                            </Box>
                            
                            {question.article_reference && (
                                <Chip
                                    label={`Ref: ${question.article_reference}`}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                        borderRadius: '12px',
                                        height: '24px',
                                        fontSize: '0.75rem',
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.2),
                                        color: theme.palette.text.secondary,
                                        maxWidth: '100%',
                                        '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }
                                    }}
                                />
                            )}
                            
                            {question.final_message && (
                                <Typography 
                                    variant="body2"
                                    sx={{ 
                                        fontWeight: 400,
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.8rem',
                                        lineHeight: 1.4,
                                        fontStyle: 'italic',
                                        mt: 0.5,
                                        backgroundColor: alpha(
                                            question.is_success ? '#4caf50' : '#f44336', 
                                            0.05
                                        ),
                                        p: 1,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: alpha(
                                            question.is_success ? '#4caf50' : '#f44336', 
                                            0.1
                                        ),
                                    }}
                                >
                                    {question.final_message}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Paper>
            );
        }

        // Renderização para questões normais
        return (
            <Paper
                sx={{
                    mb: 0.5,
                    cursor: 'grab',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    position: 'relative',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        borderColor: theme.palette.primary.main,
                    },
                    overflow: 'hidden'
                }}
                onDragStart={(e) => handleDragStart(e, question)}
                draggable
                elevation={0}
            >
                <Box sx={{ p: 1.5 }}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 1
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label="Pergunta"
                                    sx={{
                                        backgroundColor: alpha('#E3F2FD', 0.9),
                                        color: '#1976d2',
                                        borderRadius: '12px',
                                        height: '24px',
                                        fontSize: '0.75rem',
                                        border: `1px solid ${alpha('#1976d2', 0.2)}`,
                                    }}
                                />
                                <Chip
                                    label={`ID: ${question.id}`}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                        borderRadius: '12px',
                                        height: '24px',
                                        fontSize: '0.75rem',
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.2),
                                        color: theme.palette.text.secondary,
                                    }}
                                />
                                {question.count > 1 && (
                                    <Chip
                                        label={`${question.count}x`}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                            color: theme.palette.warning.main,
                                            borderRadius: '12px',
                                            height: '24px',
                                            fontSize: '0.75rem',
                                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                            fontWeight: 'bold'
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Typography 
                            variant="body2"
                            sx={{ 
                                fontWeight: 500,
                                color: theme.palette.text.primary,
                                fontSize: '0.875rem',
                                lineHeight: 1.4
                            }}
                        >
                            {question.text}
                        </Typography>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 0.5,
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start',
                        }}>
                            {Array.isArray(question.options) && question.options.map((option, index) => (
                                <Button
                                    key={index}
                                    variant="contained"
                                    sx={{
                                        backgroundColor: alpha('#E3F2FD', 0.9),
                                        color: '#1976d2',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        minWidth: '60px',
                                        padding: '4px 12px',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'nowrap',
                                        height: '24px',
                                        '&:hover': {
                                            backgroundColor: alpha('#1976d2', 0.1),
                                        }
                                    }}
                                >
                                    {option}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Paper>
        );
    };

    // Filtrar questões
    const includedQuestions = nodes.map(node => node.data).filter(q => !q.is_final);
    
    // Agrupar questões incluídas por ID e contar repetições
    const groupedIncludedQuestions = includedQuestions.reduce((acc, question) => {
        if (!acc[question.id]) {
            acc[question.id] = {
                ...question,
                count: 1
            };
        } else {
            acc[question.id].count++;
        }
        return acc;
    }, {});
    
    // Converter o objeto agrupado em array
    const uniqueIncludedQuestions = Object.values(groupedIncludedQuestions);

    // Corrigindo a lógica de filtragem para usar question_bank_id
    const availableQuestions = questionBankItems.filter(q => 
        !q.is_final && 
        !nodes.some(node => node.data.question_bank_id === q.id)
    );
    const conclusions = questionBankItems.filter(q => q.is_final);
    
    // Função para preservar o viewport - Definida antes de seu uso
    const preserveCurrentViewport = useCallback(() => {
        if (reactFlowInstance.current) {
            const viewport = reactFlowInstance.current.getViewport();
            console.log('Preservando viewport:', viewport);
            setLastViewport(viewport);
            setPreservingViewport(true);
        } else {
            console.warn('Não foi possível preservar o viewport, reactFlowInstance não disponível');
        }
    }, []);

    // Função para carregar o fluxo - Agora usa preserveCurrentViewport que já foi definida
    const fetchFlow = useCallback(async (preserveViewport = false) => {
        try {
            setLoading(true);
            
            if (preserveViewport) {
                preserveCurrentViewport();
            }
            
            if (!id || id === 'new') {
                setFlow({ name: 'Novo Fluxo', description: '', available: true });
                setNodes([]);
                setEdges([]);
                setLoading(false);
                return;
            }
            
            try {
                const response = await api.get(`/flows-new/${id}`);
                const flowData = response.data;
                
                if (!flowData) {
                    throw new Error('Dados do fluxo não encontrados');
                }
                
                setFlow({
                    id: flowData.id || parseInt(id),
                    name: flowData.name || 'Fluxo sem nome',
                    description: flowData.description || '',
                    available: flowData.available !== undefined ? flowData.available : true,
                });
                
                if (!flowData.questions || !Array.isArray(flowData.questions)) {
                    console.warn('Fluxo não possui questões ou formato inválido:', flowData);
                    setNodes([]);
                    setEdges([]);
                    setLoading(false);
                    return;
                }
                
                const flowNodes = flowData.questions.map(q => {
                    if (!q.flow_question_id) {
                        console.warn('Questão sem flow_question_id, usando id como substituto:', q);
                        q.flow_question_id = q.id;
                    }
                    
                    if (!q.next_question_logic) {
                        q.next_question_logic = {};
                    } else if (typeof q.next_question_logic === 'string') {
                        try {
                            q.next_question_logic = JSON.parse(q.next_question_logic);
                        } catch (e) {
                            console.error('Erro ao parsear next_question_logic:', e);
                            q.next_question_logic = {};
                        }
                    }
                    
                    let options = q.options;
                    if (typeof options === 'string') {
                        try {
                            options = JSON.parse(options);
                        } catch (e) {
                            console.error('Erro ao parsear options:', e);
                            options = [];
                        }
                    }
                    if (!Array.isArray(options)) {
                        console.warn('options não é um array:', options);
                        options = [];
                    }
                    
                    return {
                        id: q.id.toString(),
                        type: 'question',
                        position: { x: q.position_x || 0, y: q.position_y || 0 },
                        data: {
                            id: q.question_bank_id,
                            flow_question_id: q.id,
                            text: q.text,
                            options: options,
                            is_final: q.is_final || false,
                            is_success: q.is_success || false,
                            final_message: q.final_message || '',
                            article_reference: q.article_reference || '',
                            next_question_logic: q.next_question_logic || {},
                            isFirstQuestion: flowData.first_question_id === q.id,
                            onSetAsFirstQuestion: handleSetAsFirstQuestion,
                        },
                    };
                }).filter(node => node !== null);
                
                setNodes(flowNodes);
                
                const flowEdges = [];
                flowData.questions.forEach(q => {
                    if (!q.id) {
                        console.warn('Questão sem ID, ignorando conexões:', q);
                        return;
                    }
                    
                    let nextQuestionLogic = q.next_question_logic;
                    if (typeof nextQuestionLogic === 'string') {
                        try {
                            nextQuestionLogic = JSON.parse(nextQuestionLogic);
                        } catch (e) {
                            console.error('Erro ao parsear next_question_logic:', e);
                            nextQuestionLogic = {};
                        }
                    }
                    
                    if (!q.is_final && nextQuestionLogic && typeof nextQuestionLogic === 'object') {
                        let options = q.options;
                        if (typeof options === 'string') {
                            try {
                                options = JSON.parse(options);
                            } catch (e) {
                                console.error('Erro ao parsear options:', e);
                                options = [];
                            }
                        }
                        if (!Array.isArray(options)) {
                            console.warn('options não é um array:', options);
                            options = [];
                        }
                        
                        Object.entries(nextQuestionLogic).forEach(([optionIndex, nextId]) => {
                            if (nextId) {
                                const edgeId = `e${q.id}-${nextId}`;
                                const optionLabel = options[parseInt(optionIndex)] || `Opção ${optionIndex}`;
                                
                                flowEdges.push({
                                    id: edgeId,
                                    source: q.id.toString(),
                                    target: nextId.toString(),
                                    sourceHandle: optionIndex.toString(),
                                    label: optionLabel,
                                });
                            }
                        });
                    }
                });
                
                setEdges(flowEdges);
                
                // Se estiver preservando o viewport, não fazer nada aqui
                // O useEffect irá cuidar da restauração do viewport
            } catch (error) {
                console.error('Erro ao carregar fluxo:', error);
                showNotification(`Erro ao carregar fluxo: ${error.message || 'Erro desconhecido'}`, 'error');
                setPreservingViewport(false);
            }
        } catch (error) {
            console.error('Erro ao carregar fluxo:', error);
            showNotification(`Erro ao carregar fluxo: ${error.message || 'Erro desconhecido'}`, 'error');
            setPreservingViewport(false);
        } finally {
            setLoading(false);
        }
    }, [id, showNotification, preserveCurrentViewport]);
    
    // Carregar fluxo
    useEffect(() => {
        fetchFlow();
    }, [id]);
    
    // Carregar banco de questões
    useEffect(() => {
        const fetchQuestionBank = async () => {
            try {
                console.log('Carregando banco de questões');
                const questions = await questionBankService.list();
                console.log('Banco de questões carregado:', questions);
                
                if (!questions || !Array.isArray(questions)) {
                    console.error('Resposta inválida ao carregar banco de questões:', questions);
                    throw new Error('Formato de resposta inválido ao carregar banco de questões');
                }
                
                // Garantir que todos os campos necessários estejam presentes
                const processedQuestions = questions.map(q => ({
                    ...q,
                    options: q.options || [],
                    is_final: q.is_final || false,
                    is_success: q.is_success || false,
                    final_message: q.final_message || '',
                    article_reference: q.article_reference || '',
                }));
                
                // Ordenar questões alfabeticamente pelo título
                const sortedQuestions = [...processedQuestions].sort((a, b) => {
                    if (!a.title) return 1;
                    if (!b.title) return -1;
                    return a.title.localeCompare(b.title, 'pt-BR');
                });
                
                console.log('Questões ordenadas alfabeticamente:', sortedQuestions);
                setQuestionBankItems(sortedQuestions);
            } catch (error) {
                console.error('Erro ao carregar banco de questões:', error);
                if (showNotification) {
                    showNotification(`Erro ao carregar banco de questões: ${error.message || 'Erro desconhecido'}`, 'error');
                }
                // Definir estado padrão em caso de erro
                setQuestionBankItems([]);
            }
        };
        
        fetchQuestionBank();
    }, [showNotification]);
    
    // Manipuladores de eventos do ReactFlow
    const onNodesChange = useCallback(
        (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
        []
    );
    
    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        []
    );
    
    // Manipulador para selecionar nós
    const onNodeClick = useCallback((event, node) => {
        console.log('Nó selecionado:', node);
        setSelectedNode(node);
        setSelectedEdge(null);
    }, []);
    
    // Manipulador para selecionar arestas
    const onEdgeClick = useCallback((event, edge) => {
        console.log('Aresta selecionada:', edge);
        setSelectedEdge(edge);
        setSelectedNode(null);
    }, []);
    
    // Função para restaurar o viewport
    const restoreViewport = useCallback(() => {
        if (reactFlowInstance.current && lastViewport && preservingViewport) {
            // Aumentar o tempo de espera para garantir que os nós sejam renderizados completamente
            setTimeout(() => {
                if (reactFlowInstance.current) {
                    console.log('Restaurando viewport:', lastViewport);
                    reactFlowInstance.current.setViewport(lastViewport);
                    setPreservingViewport(false);
                }
            }, 200); // Aumentar para 200ms para dar mais tempo para a renderização
        } else if (preservingViewport) {
            // Se não temos as condições necessárias para restaurar o viewport,
            // desativar a preservação para evitar que fique preso
            console.warn('Não foi possível restaurar o viewport, condições não atendidas');
            setPreservingViewport(false);
        }
    }, [lastViewport, preservingViewport, setPreservingViewport]);

    // Atualizar o useEffect do ReactFlow para usar o novo sistema de viewport
    useEffect(() => {
        if (preservingViewport && nodes.length > 0) {
            console.log('Detectada mudança nos nós/arestas com preservação de viewport ativa');
            restoreViewport();
        }
    }, [nodes, edges, preservingViewport, restoreViewport]);

    // Modificar o onPaneClick para preservar o viewport
    const onPaneClick = useCallback((event) => {
        if (!preservingViewport) {
            setSelectedNode(null);
            setSelectedEdge(null);
        }
    }, [preservingViewport]);
    
    // Função onConnect modificada para preservar o viewport
    const onConnect = useCallback(
        async (connection) => {
            console.log('Criando conexão:', connection);
            
            // Verificar se a conexão é válida
            if (!connection.source || !connection.target) {
                console.error('Conexão inválida:', connection);
                return;
            }

            // Encontrar o nó de origem e a opção selecionada
            const sourceNode = nodes.find(n => n.id === connection.source);
            if (!sourceNode) {
                console.error(`Nó de origem não encontrado: ${connection.source}`);
                return;
            }

            if (sourceNode.data.is_final) {
                console.warn('Não é possível conectar a partir de um nó final');
                if (showNotification) {
                    showNotification('Não é possível conectar a partir de um nó final', 'warning');
                }
                return;
            }
            
            // Verificar se o nó tem opções
            if (!sourceNode.data.options || !Array.isArray(sourceNode.data.options) || sourceNode.data.options.length === 0) {
                console.warn('Nó de origem não tem opções:', sourceNode);
                if (showNotification) {
                    showNotification('Nó de origem não tem opções definidas', 'warning');
                }
                return;
            }
            
            // Determinar qual opção foi usada para a conexão
            const optionIndex = connection.sourceHandle || '0';
            
            // Criar uma aresta com a opção como rótulo
            const newEdge = {
                ...connection,
                id: `e${connection.source}-${connection.target}`,
                label: sourceNode.data.options[parseInt(optionIndex)] || sourceNode.data.options[0],
            };
            
            console.log('Nova aresta criada:', newEdge);
            
            // Adicionar a nova aresta ao estado sem resetar o viewport
            setEdges(eds => {
                const newEdges = addEdge(newEdge, eds);
                return newEdges;
            });
            
            // Atualizar a lógica de próxima questão do nó de origem
            const updatedNodes = nodes.map(node => {
                if (node.id === connection.source) {
                    // Criar ou atualizar a lógica de próxima questão
                    const nextQuestionLogic = { ...(node.data.next_question_logic || {}) };
                    nextQuestionLogic[optionIndex] = parseInt(connection.target);
                    
                    const updatedNode = {
                        ...node,
                        data: {
                            ...node.data,
                            next_question_logic: nextQuestionLogic,
                        },
                    };
                    
                    console.log('Nó atualizado com nova lógica:', updatedNode);
                    
                    return updatedNode;
                }
                return node;
            });
            
            // Atualizar o estado dos nós sem resetar o viewport
            setNodes(updatedNodes);
            
            // Salvar o fluxo para persistir as conexões
            if (flow.id) {
                try {
                    setSaving(true);
                    
                    // Preparar os dados para salvar
                    const questionsToSave = updatedNodes.map(node => ({
                        id: parseInt(node.id), // ID da questão no fluxo
                        question_bank_id: node.data.id, // ID da questão no banco
                        position_x: node.position.x,
                        position_y: node.position.y,
                        next_question_logic: node.data.next_question_logic || {},
                    }));
                    
                    console.log('Salvando fluxo com questões:', questionsToSave);
                    
                    // Chamar a API para salvar o fluxo
                    const response = await api.put(`/flows-new/${flow.id}`, {
                        ...flow,
                        questions: questionsToSave,
                    });
                    
                    console.log('Fluxo salvo após criar conexão:', response.data);
                    
                    if (showNotification) {
                        showNotification('Conexão salva com sucesso', 'success');
                    }
                    
                    // Recarregar o fluxo para garantir que tudo esteja sincronizado
                    // Mas não resetar o viewport
                    const currentFlow = await flowService.get(flow.id);
                    setFlow(currentFlow);
                    
                    // Não chamar fetchFlow() para evitar resetar o viewport
                } catch (error) {
                    console.error('Erro ao salvar fluxo após criar conexão:', error);
                    if (showNotification) {
                        showNotification('Erro ao salvar conexão', 'error');
                    }
                } finally {
                    setSaving(false);
                }
            } else {
                console.warn('Fluxo não salvo, não é possível persistir a conexão');
                if (showNotification) {
                    showNotification('Salve o fluxo antes de criar conexões', 'warning');
                }
            }
        },
        [nodes, showNotification, flow]
    );
    
    // Abrir diálogo para adicionar questão
    const handleOpenDialog = (position) => {
        setNodePosition(position);
        setOpenDialog(true);
    };
    
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedQuestion('');
    };
    
    // Função para lidar com o início do drag
    const handleDragStart = (event, question) => {
        event.dataTransfer.setData('application/json', JSON.stringify(question));
    };

    // Função para lidar com o drop
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        
        try {
            const questionData = JSON.parse(event.dataTransfer.getData('application/json'));
            const { clientX, clientY } = event;
            const reactFlowBounds = document.querySelector('.react-flow').getBoundingClientRect();
            const reactFlowInstance = document.querySelector('.react-flow__viewport');
            
            // Extrair a transformação atual do viewport
            const transform = reactFlowInstance.style.transform;
            const matches = transform.match(/translate\((.*?)px, (.*?)px\) scale\((.*?)\)/);
            
            if (!matches) return;
            
            const [, translateX, translateY, scale] = matches.map(Number);
            
            // Calcular a posição real considerando o scroll e zoom
            const position = {
                x: (clientX - reactFlowBounds.left - translateX) / scale,
                y: (clientY - reactFlowBounds.top - translateY) / scale
            };
            
            // Adiciona a questão ao fluxo na posição do drop
            handleAddQuestion(questionData, position);
        } catch (error) {
            console.error('Erro ao processar drop:', error);
        }
    }, []);

    // Função para lidar com o dragover
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    // Função modificada para adicionar questão
    const handleAddQuestion = useCallback(async (questionData, position) => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de adicionar questões', 'warning');
                return;
            }
            
            // Preservar o viewport atual
            preserveCurrentViewport();
            
            console.log('Adicionando questão:', questionData, 'na posição:', position);
            
            // Verificar se a questão já existe no fluxo
            const questionExists = nodes.some(node => 
                node.data.question_bank_id === questionData.id
            );
            
            if (questionExists) {
                showNotification('Esta questão já existe no fluxo', 'warning');
                setPreservingViewport(false); // Importante: desativar a preservação se não adicionarmos
                return;
            }
            
            try {
                // Adicionar a questão ao fluxo
                const response = await api.post(`/flows-new/${id}/questions`, {
                    question_bank_id: questionData.id,
                    position_x: position.x,
                    position_y: position.y
                });
                
                // Em vez de recarregar o fluxo inteiro, adicionar o nó diretamente ao estado
                const newNode = {
                    id: response.data.id.toString(),
                    type: 'question',
                    position: { x: position.x, y: position.y },
                    data: {
                        id: questionData.id,
                        question_bank_id: questionData.id,
                        flow_question_id: response.data.id,
                        text: questionData.text,
                        options: questionData.options,
                        is_final: questionData.is_final || false,
                        is_success: questionData.is_success,
                        final_message: questionData.final_message || '',
                        article_reference: questionData.article_reference || '',
                        next_question_logic: response.data.next_question_logic || {},
                        isFirstQuestion: false,
                        // Usar uma função anônima em vez de referenciar diretamente
                        onSetAsFirstQuestion: (nodeId) => {
                            // Esta função será substituída pela real quando o nó for renderizado
                            console.log('Definindo como primeira questão:', nodeId);
                        },
                    },
                };
                
                // Adicionar o novo nó ao estado
                setNodes(prevNodes => [...prevNodes, newNode]);
                
                // Desativar a preservação do viewport, pois não estamos recarregando o fluxo
                setPreservingViewport(false);
                
                showNotification('Questão adicionada com sucesso!', 'success');
            } catch (error) {
                console.error('Erro ao adicionar questão:', error);
                showNotification(`Erro ao adicionar questão: ${error.message || 'Erro desconhecido'}`, 'error');
                setPreservingViewport(false); // Importante: desativar a preservação em caso de erro
            }
        } catch (error) {
            console.error('Erro ao adicionar questão:', error);
            showNotification(`Erro ao adicionar questão: ${error.message || 'Erro desconhecido'}`, 'error');
            setPreservingViewport(false); // Importante: desativar a preservação em caso de erro
        }
    }, [id, nodes, showNotification, preserveCurrentViewport, setPreservingViewport]);
    
    // Função para atualizar as conexões entre os nós clonados após o salvamento
    const updateClonedNodesConnections = useCallback((oldNodes, newNodes, oldEdges) => {
        // Criar um mapeamento de IDs temporários para IDs reais
        const idMapping = {};
        
        // Mapear os IDs temporários para os IDs reais
        oldNodes.forEach(oldNode => {
            if (oldNode.id.startsWith('clone-')) {
                // Obter o question_bank_id do nó antigo
                const oldQuestionBankId = oldNode.data.question_bank_id || oldNode.data.id;
                
                // Encontrar o nó correspondente nos novos nós
                const newNode = newNodes.find(
                    n => {
                        const newQuestionBankId = n.data.question_bank_id || n.data.id;
                        return newQuestionBankId === oldQuestionBankId &&
                            Math.abs(n.position.x - oldNode.position.x) < 10 &&
                            Math.abs(n.position.y - oldNode.position.y) < 10;
                    }
                );
                
                if (newNode) {
                    idMapping[oldNode.id] = newNode.id;
                    console.log(`Mapeamento: ${oldNode.id} -> ${newNode.id}`);
                } else {
                    console.warn(`Não foi possível encontrar o nó correspondente para ${oldNode.id}`);
                }
            }
        });
        
        console.log('Mapeamento de IDs temporários para reais:', idMapping);
        
        // Atualizar as conexões entre os nós clonados
        const updatedEdges = [];
        
        oldEdges.forEach(edge => {
            // Se a origem ou destino for um nó clonado, atualizar para o ID real
            if (edge.source.startsWith('clone-') || edge.target.startsWith('clone-')) {
                const newSource = idMapping[edge.source] || edge.source;
                const newTarget = idMapping[edge.target] || edge.target;
                
                // Se não conseguimos mapear a origem ou destino, pular esta aresta
                if (!newSource || !newTarget || 
                    (edge.source.startsWith('clone-') && !idMapping[edge.source]) || 
                    (edge.target.startsWith('clone-') && !idMapping[edge.target])) {
                    console.warn(`Pulando aresta ${edge.id} porque não foi possível mapear origem ou destino`);
                    return;
                }
                
                // Atualizar a lógica de próxima questão no nó de origem
                const sourceNode = newNodes.find(n => n.id === newSource);
                if (sourceNode) {
                    if (!sourceNode.data.next_question_logic) {
                        sourceNode.data.next_question_logic = {};
                    }
                    
                    // Encontrar a resposta correspondente
                    const answerKey = Object.keys(edge.data?.next_question_logic || {}).find(
                        key => edge.data.next_question_logic[key] === edge.target
                    );
                    
                    if (answerKey) {
                        sourceNode.data.next_question_logic[answerKey] = newTarget;
                    } else {
                        // Se não encontrar uma resposta específica, usar a resposta padrão (0)
                        sourceNode.data.next_question_logic['0'] = newTarget;
                    }
                }
                
                // Adicionar a nova aresta
                updatedEdges.push({
                    ...edge,
                    id: `edge-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                    source: newSource,
                    target: newTarget
                });
            } else {
                updatedEdges.push(edge);
            }
        });
        
        return { updatedNodes: newNodes, updatedEdges };
    }, []);

    // Salvar fluxo
    const handleSave = async () => {
        try {
            setSaving(true);
            
            // Preservar o viewport atual
            preserveCurrentViewport();
            
            console.log('Salvando fluxo:', flow);
            console.log('Nós para salvar:', nodes);
            
            // Verificar se há nós clonados
            const hasClonedNodes = nodes.some(node => node.id.startsWith('clone-') || node.data.id === undefined);
            console.log('Há nós clonados?', hasClonedNodes);
            
            // Salvar os nós e arestas atuais antes de recarregar
            const oldNodes = [...nodes];
            const oldEdges = [...edges];
            
            // Encontrar a questão mais ao topo (menor position_y) que não seja uma conclusão
            let topQuestionId = flow.first_question_id;
            
            if (!topQuestionId) {
                let minY = Infinity;
                let topNode = null;
                
                // Filtrar nós que não são conclusões
                const nonFinalNodes = nodes.filter(node => !node.data.is_final);
                
                if (nonFinalNodes.length > 0) {
                    // Encontrar o nó com menor position_y
                    for (const node of nonFinalNodes) {
                        if (node.position.y < minY) {
                            minY = node.position.y;
                            topNode = node;
                        }
                    }
                    
                    if (topNode) {
                        topQuestionId = parseInt(topNode.id);
                        console.log(`Definindo automaticamente a questão ${topQuestionId} como primeira (posição Y: ${minY})`);
                    }
                }
            }
            
            // Preparar dados para salvar
            const questionsToSave = nodes.map(node => {
                // Verificar se é um nó clonado (sem ID definido no data)
                const isClonedNode = node.id.startsWith('clone-') || node.data.id === undefined;
                
                // Obter o question_bank_id correto
                const question_bank_id = node.data.question_bank_id || node.data.id;
                
                // Importante: garantir que estamos enviando apenas question_bank_id para nós clonados
                // O backend não deve tentar criar novas questões no banco
                return {
                    // Para nós clonados, não enviar o ID para que o backend gere um novo
                    // Para nós existentes, enviar o ID normalmente
                    id: isClonedNode ? undefined : parseInt(node.id),
                    // Usar o question_bank_id do data para referenciar a questão correta
                    question_bank_id: question_bank_id,
                    position_x: node.position.x,
                    position_y: node.position.y,
                    next_question_logic: node.data.next_question_logic || {},
                };
            }).filter(question => question.question_bank_id); // Filtrar questões sem question_bank_id
            
            const flowData = {
                ...flow,
                first_question_id: topQuestionId, // Definir a primeira questão
                questions: questionsToSave,
            };
            
            console.log('Dados do fluxo para salvar:', flowData);
            console.log('Questões para salvar:', questionsToSave);
            
            let response;
            
            try {
                if (id === 'new') {
                    // Criar novo fluxo
                    response = await api.post('/flows-new/', flowData);
                    
                    // Atualizar o ID na URL para que os próximos salvamentos sejam atualizações
                    navigate(`/flows-new/${response.data.id}/edit`, { replace: true });
                    
                    // Atualizar o estado com os dados retornados
                    setFlow({
                        id: response.data.id,
                        name: response.data.name,
                        description: response.data.description,
                        available: response.data.available,
                    });
                    
                    // Recarregar o fluxo para obter os novos IDs gerados pelo backend
                    await fetchFlow(true);
                } else {
                    // Atualizar fluxo existente
                    response = await api.put(`/flows-new/${id}`, flowData);
                    
                    // Atualizar o estado com os dados retornados
                    setFlow({
                        id: response.data.id,
                        name: response.data.name,
                        description: response.data.description,
                        available: response.data.available,
                    });
                    
                    // Se não há nós clonados, não precisamos recarregar o fluxo
                    // Apenas atualizamos o estado para refletir a primeira questão
                    if (!hasClonedNodes) {
                        // Atualizar os nós para refletir a nova primeira questão
                        setNodes(prevNodes => 
                            prevNodes.map(node => ({
                                ...node,
                                data: {
                                    ...node.data,
                                    isFirstQuestion: parseInt(node.id) === topQuestionId
                                }
                            }))
                        );
                        
                        // Desativar a preservação do viewport
                        setPreservingViewport(false);
                    } else {
                        // Se havia nós clonados, precisamos recarregar o fluxo
                        // para obter os novos IDs gerados pelo backend
                        await fetchFlow(true);
                        
                        console.log('Atualizando conexões dos nós clonados...');
                        const { updatedNodes, updatedEdges } = updateClonedNodesConnections(oldNodes, nodes, oldEdges);
                        
                        // Atualizar os nós e arestas com as novas conexões
                        setNodes(updatedNodes);
                        setEdges(updatedEdges);
                        
                        // Preparar dados para o segundo salvamento
                        const updatedQuestionsToSave = updatedNodes.map(node => ({
                            id: parseInt(node.id),
                            question_bank_id: node.data.question_bank_id || node.data.id,
                            position_x: node.position.x,
                            position_y: node.position.y,
                            next_question_logic: node.data.next_question_logic || {},
                        })).filter(question => question.question_bank_id); // Filtrar questões sem question_bank_id
                        
                        console.log('Salvando novamente com conexões atualizadas...');
                        console.log('Questões atualizadas para salvar:', updatedQuestionsToSave);
                        
                        // Salvar novamente para atualizar as conexões
                        await api.put(`/flows-new/${id}`, {
                            ...flow,
                            questions: updatedQuestionsToSave,
                        });
                    }
                }
                
                // Mostrar notificação de sucesso
                if (showNotification) {
                    showNotification('Fluxo salvo com sucesso', 'success');
                }
            } catch (error) {
                console.error('Erro ao salvar fluxo:', error);
                console.error('Detalhes do erro:', error.response?.data);
                if (showNotification) {
                    showNotification(`Erro ao salvar fluxo: ${error.message || 'Erro desconhecido'}`, 'error');
                }
                setPreservingViewport(false); // Desativar a preservação em caso de erro
                throw error; // Propagar o erro para o tratamento externo
            }
        } catch (error) {
            console.error('Erro geral ao salvar fluxo:', error);
            if (showNotification) {
                showNotification(`Erro ao salvar fluxo: ${error.message || 'Erro desconhecido'}`, 'error');
            }
            setPreservingViewport(false); // Desativar a preservação em caso de erro
        } finally {
            setSaving(false);
        }
    };
    
    // Remover questão do fluxo
    const handleRemoveQuestion = async (nodeId) => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de remover questões', 'warning');
                return;
            }
            
            setSaving(true);
            
            console.log(`Removendo questão com ID ${nodeId} do fluxo ${flow.id}`);
            
            // Encontrar o nó a ser removido
            const nodeToRemove = nodes.find(n => n.id === nodeId);
            if (!nodeToRemove) {
                console.error(`Nó com ID ${nodeId} não encontrado`);
                if (showNotification) {
                    showNotification('Questão não encontrada', 'error');
                }
                return;
            }
            
            // Remover questão do fluxo
            await api.delete(`/flows-new/${flow.id}/questions/${nodeId}`);
            
            console.log(`Questão com ID ${nodeId} removida com sucesso`);
            
            // Remover o nó do estado
            setNodes(prevNodes => prevNodes.filter(n => n.id !== nodeId));
            
            // Remover arestas conectadas ao nó
            setEdges(prevEdges => prevEdges.filter(e => e.source !== nodeId && e.target !== nodeId));
            
            // Atualizar a lógica de próxima questão dos nós que apontam para o nó removido
            setNodes(prevNodes => prevNodes.map(node => {
                if (node.data.next_question_logic) {
                    const updatedLogic = { ...node.data.next_question_logic };
                    let changed = false;
                    
                    // Verificar se alguma opção aponta para o nó removido
                    Object.entries(updatedLogic).forEach(([option, targetId]) => {
                        if (targetId.toString() === nodeId) {
                            delete updatedLogic[option];
                            changed = true;
                        }
                    });
                    
                    if (changed) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                next_question_logic: updatedLogic,
                            },
                        };
                    }
                }
                return node;
            }));
            
            // Limpar o nó selecionado
            setSelectedNode(null);
            
            if (showNotification) {
                showNotification('Questão removida com sucesso', 'success');
            }
            
            // Atualizar o banco de questões sem recarregar o fluxo completo
            await fetchQuestionBank();
        } catch (error) {
            console.error('Erro ao remover questão:', error);
            if (showNotification) {
                showNotification(`Erro ao remover questão: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        } finally {
            setSaving(false);
        }
    };
    
    // Editar questão
    const handleEditQuestion = async (node) => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de editar questões', 'warning');
                return;
            }
            
            // Mostrar loading
            setSaving(true);
            
            try {
                // Carregar os dados completos da questão do banco
                const response = await api.get(`/question-bank/${node.data.id}`);
                
                console.log('Dados completos da questão:', response.data);
                
                // Verificar se existe dynamic_text
                if (!response.data.dynamic_text || Object.keys(response.data.dynamic_text).length === 0) {
                    // Se não existir dynamic_text, criar um objeto vazio
                    response.data.dynamic_text = {};
                    
                    // Pré-inicializar com tipo 'act_type' para todas as variáveis no texto
                    const text = response.data.text || '';
                    const matches = text.match(/\{([^}]+)\}/g) || [];
                    const variables = matches.map(match => match.slice(1, -1));
                    
                    // Para cada variável encontrada, inicializar com tipo 'act_type'
                    variables.forEach(variable => {
                        response.data.dynamic_text[variable] = { type: 'act_type' };
                    });
                }
                
                // Verificar se temos informações sobre a questão anterior
                if (response.data.previous_question) {
                    console.log('Questão tem questão anterior:', response.data.previous_question);
                }
                
                // Atualizar o node com os dados completos
                const updatedNode = {
                    ...node,
                    data: {
                        ...node.data,
                        ...response.data,
                        // Garantir que as referências das propriedades do nó original sejam mantidas
                        flow_question_id: node.data.flow_question_id,
                        position: node.data.position,
                        style: node.data.style,
                        // Outras propriedades específicas do nó
                        onSetAsFirstQuestion: node.data.onSetAsFirstQuestion,
                        onDelete: node.data.onDelete
                    }
                };
                
                // Definir o nó atualizado
                setSelectedNodeForEdit(updatedNode);
                
                // Verificar se é uma conclusão baseado na propriedade is_final
                if (updatedNode.data.is_final === true) {
                    console.log("Abrindo modal de conclusão para editar:", updatedNode.data);
                    console.log("Dados da conclusão:", {
                        id: updatedNode.data.id,
                        text: updatedNode.data.text || '',
                        options: updatedNode.data.options || [],
                        is_final: true,
                        article_reference: updatedNode.data.article_reference || '',
                        dynamic_text: updatedNode.data.dynamic_text || {},
                        previous_question: updatedNode.data.previous_question || null,
                        final_message: updatedNode.data.final_message || '',
                        is_success: updatedNode.data.is_success
                    });
                    
                    // Fechar todos os outros modais
                    closeAllModals();
                    
                    // Abrir o modal de edição de conclusão
                    setIsConclusionFormOpen(true);
                    setSelectedNodeForEdit(updatedNode);
                } else {
                    console.log("Abrindo modal de questão para editar:", updatedNode.data);
                    
                    // Fechar todos os outros modais
                    closeAllModals();
                    
                    // Abrir o modal de edição
                    setIsQuestionFormOpen(true);
                    setSelectedNodeForEdit(updatedNode);
                }
                
            } catch (error) {
                console.error('Erro ao carregar dados da questão:', error);
                showNotification(`Erro ao carregar dados da questão: ${error.message || 'Erro desconhecido'}`, 'error');
            } finally {
                setSaving(false);
            }
        } catch (error) {
            console.error('Erro ao editar questão:', error);
            if (showNotification) {
                showNotification(`Erro ao editar questão: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        }
    };
    
    // Clonar questão
    const handleCloneQuestion = async (node) => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de clonar questões', 'warning');
                return;
            }
            
            setSaving(true);
            
            // Obter a questão do banco
            const questionResponse = await api.get(`/question-bank/${node.data.id}`);
            const question = questionResponse.data;
            
            // Clonar a questão no banco
            const cloneResponse = await api.post('/question-bank/', {
                ...question,
                text: `${question.text} (Cópia)`,
                id: undefined,
                created_at: undefined,
                updated_at: undefined,
            });
            
            const clonedQuestion = cloneResponse.data;
            
            // Adicionar a nova questão ao fluxo
            const response = await api.post(`/flows-new/${id}/questions`, {
                question_bank_id: clonedQuestion.id,
                position_x: node.position.x + 300,
                position_y: node.position.y,
            });
            
            console.log('Questão adicionada ao fluxo:', response.data);
            
            if (showNotification) {
                showNotification('Questão adicionada com sucesso', 'success');
            }
            
            // Recarregar o fluxo para mostrar a nova questão
            console.log('Recarregando fluxo após adicionar questão');
            await fetchFlow();
        } catch (error) {
            console.error('Erro ao adicionar questão:', error);
            if (showNotification) {
                showNotification(`Erro ao adicionar questão: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        } finally {
            setSaving(false);
        }
    };
    
    // Definir questão como primeira do fluxo
    const handleSetAsFirstQuestion = async (nodeId) => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de definir a primeira questão', 'warning');
                return;
            }
            
            setSaving(true);
            
            console.log(`Definindo questão ${nodeId} como primeira do fluxo ${id}`);
            
            // Salvar o fluxo
            const response = await api.put(`/flows-new/${id}`, {
                first_question_id: nodeId,
            });
            
            console.log('Fluxo atualizado com nova primeira questão:', response.data);
            
            if (showNotification) {
                showNotification('Primeira questão definida com sucesso', 'success');
            }
            
            // Atualizar o estado do fluxo
            setFlow(prev => ({
                ...prev,
                first_question_id: parseInt(nodeId)
            }));
            
            // Atualizar os nós para refletir a nova primeira questão
            setNodes(prevNodes => 
                prevNodes.map(node => ({
                    ...node,
                    data: {
                        ...node.data,
                        isFirstQuestion: node.id === nodeId.toString()
                    }
                }))
            );
        } catch (error) {
            console.error('Erro ao definir primeira questão:', error);
            if (showNotification) {
                showNotification(`Erro ao definir primeira questão: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        } finally {
            setSaving(false);
        }
    };
    
    // Remover aresta
    const handleRemoveEdge = async (edgeId) => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de remover conexões', 'warning');
                return;
            }
            
            setSaving(true);
            
            console.log(`Removendo aresta com ID ${edgeId}`);
            
            // Encontrar a aresta
            const edge = edges.find(e => e.id === edgeId);
            if (!edge) {
                console.error(`Aresta com ID ${edgeId} não encontrada`);
                return;
            }
            
            // Encontrar o nó de origem
            const sourceNode = nodes.find(n => n.id === edge.source);
            if (!sourceNode) {
                console.error(`Nó de origem com ID ${edge.source} não encontrado`);
                return;
            }
            
            // Remover a conexão da lógica de próxima questão
            const updatedNode = { ...sourceNode };
            const option = edge.sourceHandle || '0';
            
            // Criar uma cópia profunda da lógica de próxima questão
            const updatedLogic = { ...updatedNode.data.next_question_logic };
            delete updatedLogic[option];
            
            // Atualizar o nó
            updatedNode.data = {
                ...updatedNode.data,
                next_question_logic: updatedLogic
            };
            
            // Atualizar os nós
            const updatedNodes = nodes.map(n => 
                n.id === updatedNode.id ? updatedNode : n
            );
            
            // Atualizar as arestas
            const updatedEdges = edges.filter(e => e.id !== edgeId);
            
            // Atualizar o estado
            setNodes(updatedNodes);
            setEdges(updatedEdges);
            
            // Preparar os dados para salvar
            const updatedQuestions = updatedNodes.map(node => ({
                id: parseInt(node.id),
                question_bank_id: node.data.id,
                position_x: node.position.x,
                position_y: node.position.y,
                next_question_logic: node.data.next_question_logic || {},
            }));
            
            // Salvar o fluxo
            const response = await api.put(`/flows-new/${id}`, {
                questions: updatedQuestions
            });
            
            console.log('Fluxo atualizado após remover conexão:', response.data);
            
            if (showNotification) {
                showNotification('Conexão removida com sucesso', 'success');
            }
        } catch (error) {
            console.error('Erro ao remover conexão:', error);
            if (showNotification) {
                showNotification(`Erro ao remover conexão: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        } finally {
            setSaving(false);
        }
    };
    
    // Criar nova questão
    const handleCreateNewQuestion = async () => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de criar questões', 'warning');
                return;
            }
            
            setSaving(true);
            
            // Criar nova questão no banco
            const newQuestion = await api.post('/question-bank/', {
                text: 'Nova Questão',
                options: ['Sim', 'Não'],
                is_final: false,
                is_success: false,
                final_message: '',
                article_reference: '',
            });
            
            console.log('Nova questão criada no banco:', newQuestion.data);
            
            // Obter a posição atual do viewport
            const viewport = reactFlowInstance.current?.getViewport();
            
            // Calcular posição para a nova questão baseada no centro da viewport atual
            const position = { x: 100, y: 100 };
            
            if (viewport) {
                const { x, y, zoom } = viewport;
                const flowContainer = document.querySelector('.react-flow');
                if (flowContainer) {
                    const { width, height } = flowContainer.getBoundingClientRect();
                    position.x = (-x + width / 2) / zoom;
                    position.y = (-y + height / 2) / zoom;
                }
            } else if (nodes.length > 0) {
                // Fallback: posicionar próximo ao nó mais à direita
                const rightmostNode = nodes.reduce((max, node) => 
                    node.position.x > max.position.x ? node : max, nodes[0]);
                
                position.x = rightmostNode.position.x + 300;
                position.y = rightmostNode.position.y;
            }
            
            // Adicionar a questão ao fluxo
            const response = await api.post(`/flows-new/${id}/questions`, {
                question_bank_id: newQuestion.data.id,
                position_x: position.x,
                position_y: position.y,
            });
            
            console.log('Nova questão adicionada ao fluxo:', response.data);
            
            if (showNotification) {
                showNotification('Nova questão criada com sucesso', 'success');
            }
            
            // Recarregar o fluxo para mostrar a nova questão, preservando o viewport
            console.log('Recarregando fluxo após criar nova questão');
            await fetchFlow(true);
        } catch (error) {
            console.error('Erro ao criar nova questão:', error);
            if (showNotification) {
                showNotification(`Erro ao criar nova questão: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        } finally {
            setSaving(false);
        }
    };
    
    // Criar nova conclusão
    const handleCreateNewConclusion = async (isSuccess) => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de criar conclusões', 'warning');
                return;
            }
            
            setSaving(true);
            
            // Criar nova conclusão no banco
            const newConclusion = {
                text: isSuccess ? 'Conclusão Positiva' : 'Conclusão Negativa',
                options: JSON.stringify([]),
                is_final: true,
                is_success: isSuccess,
                final_message: isSuccess 
                    ? 'O ato pode ser praticado.'
                    : 'O ato não pode ser praticado.',
                article_reference: '',
            };
            
            const conclusionResponse = await api.post('/question-bank/', newConclusion);
            const newConclusionBank = conclusionResponse.data;
            
            console.log('Nova conclusão criada no banco:', newConclusionBank);
            
            // Obter a posição atual do viewport
            const viewport = reactFlowInstance.current?.getViewport();
            
            // Calcular posição para a nova conclusão baseada no centro da viewport atual
            const position = { x: 100, y: 100 };
            
            if (viewport) {
                const { x, y, zoom } = viewport;
                const flowContainer = document.querySelector('.react-flow');
                if (flowContainer) {
                    const { width, height } = flowContainer.getBoundingClientRect();
                    position.x = (-x + width / 2) / zoom;
                    position.y = (-y + height / 2) / zoom;
                }
            } else if (nodes.length > 0) {
                // Fallback: posicionar próximo ao nó mais à direita
                const rightmostNode = nodes.reduce((max, node) => 
                    node.position.x > max.position.x ? node : max, nodes[0]);
                
                position.x = rightmostNode.position.x + 300;
                position.y = rightmostNode.position.y;
            }
            
            // Adicionar a nova conclusão ao fluxo
            const response = await api.post(`/flows-new/${id}/questions`, {
                question_bank_id: newConclusionBank.id,
                position_x: position.x,
                position_y: position.y,
            });
            
            console.log('Nova conclusão adicionada ao fluxo:', response.data);
            
            if (showNotification) {
                showNotification('Nova conclusão criada com sucesso', 'success');
            }
            
            // Recarregar o fluxo para mostrar a nova conclusão, preservando o viewport
            console.log('Recarregando fluxo após criar nova conclusão');
            await fetchFlow(true);
        } catch (error) {
            console.error('Erro ao criar nova conclusão:', error);
            if (showNotification) {
                showNotification(`Erro ao criar nova conclusão: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        } finally {
            setSaving(false);
        }
    };
    
    // Visualizar fluxo
    const handlePreview = async () => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de pré-visualizar', 'warning');
                return;
            }
            
            // Salvar o fluxo antes de visualizar para garantir que todas as alterações estejam salvas
            await handleSave();
            
            // Redirecionar para a página de visualização
            navigate(`/flows-new/${flow.id}/preview`);
        } catch (error) {
            console.error('Erro ao visualizar fluxo:', error);
            if (showNotification) {
                showNotification(`Erro ao visualizar fluxo: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        }
    };
    
    // Voltar para a lista de fluxos
    const handleBack = async () => {
        try {
            // Perguntar se deseja salvar as alterações antes de voltar
            if (flow.id && !saving) {
                const confirmSave = window.confirm('Deseja salvar as alterações antes de voltar?');
                if (confirmSave) {
                    await handleSave();
                }
            }
            
            // Redirecionar para a lista de fluxos
            navigate('/flows-new');
        } catch (error) {
            console.error('Erro ao voltar para a lista de fluxos:', error);
            if (showNotification) {
                showNotification(`Erro ao salvar alterações: ${error.message || 'Erro desconhecido'}`, 'error');
            }
        }
    };

    // Clonar o fluxo atual
    const handleCloneFlow = async () => {
        try {
            if (id === 'new') {
                showNotification('Salve o fluxo antes de cloná-lo', 'warning');
                return;
            }

            setLoading(true);
            console.log('Clonando fluxo:', id);

            // Usar o endpoint específico de clonagem
            const response = await api.post(`/flows-new/${id}/clone`, {
                new_name: `${flow.name} (Cópia)`
            });
            
            const newFlow = response.data;
            console.log('Fluxo clonado com sucesso:', newFlow);

            showNotification('Fluxo clonado com sucesso!', 'success');
            
            // Navegar para o novo fluxo
            navigate(`/flows-new/${newFlow.id}/edit`);
        } catch (error) {
            console.error('Erro ao clonar fluxo:', error);
            showNotification(`Erro ao clonar fluxo: ${error.message || 'Erro desconhecido'}`, 'error');
        } finally {
            setLoading(false);
        }
    };
    
    // Função para abrir o modal de criação
    const handleOpenCreateModal = (type) => {
        if (id === 'new') {
            showNotification('Salve o fluxo antes de adicionar questões', 'warning');
            return;
        }
        
        // Capturar a posição atual do viewport para uso posterior
        if (reactFlowInstance.current) {
            // Armazenar o viewport atual em uma variável de estado
            const currentViewport = reactFlowInstance.current.getViewport();
            setLastViewport(currentViewport);
            console.log('Viewport capturado para nova criação:', currentViewport);
        }
        
        // Fechar todos os outros modais
        setIsQuestionFormOpen(false);
        setIsConclusionFormOpen(false);
        setSelectedNodeForEdit(null);
        
        // Abrir o modal de criação
        setCreateModalType(type);
        setOpenCreateModal(true);
    };

    // Função auxiliar para fechar todos os modais
    const closeAllModals = () => {
        console.log("Fechando todos os modais");
        setOpenCreateModal(false);
        setIsQuestionFormOpen(false);
        setIsConclusionFormOpen(false);
        setSelectedNodeForEdit(null);
        setCreateModalType('question');
    };

    // Função para fechar o modal de criação
    const handleCloseCreateModal = () => {
        console.log("Fechando modal de criação");
        closeAllModals();
        
        if (!preservingViewport) {
            setLastViewport(null);
        }
    };

    // Função para salvar nova questão/conclusão
    const handleSaveNewQuestion = async (questionData) => {
        try {
            setSaving(true);
            preserveCurrentViewport();

            // Preparar os dados da questão/conclusão
            const dataToSave = {
                ...questionData,
                options: questionData.is_final ? [] : (questionData.options || []),
                is_final: questionData.is_final || false,
                is_success: questionData.is_success,
                final_message: questionData.final_message || '',
                article_reference: questionData.article_reference || '',
                dynamic_text: questionData.dynamic_text || {}
            };

            // Criar a questão/conclusão no banco
            const questionResponse = await api.post('/question-bank/', dataToSave);
            const newQuestionBank = questionResponse.data;
            
            console.log('Nova questão/conclusão criada no banco:', newQuestionBank);
            
            // Usar o viewport salvo anteriormente ou obter o atual
            const viewport = lastViewport || reactFlowInstance.current?.getViewport();
            
            // Calcular posição para o novo nó baseada no centro da viewport atual
            const position = { x: 100, y: 100 };
            
            if (viewport) {
                const { x, y, zoom } = viewport;
                const flowContainer = document.querySelector('.react-flow');
                if (flowContainer) {
                    const { width, height } = flowContainer.getBoundingClientRect();
                    position.x = (-x + width / 2) / zoom;
                    position.y = (-y + height / 2) / zoom;
                }
            } else if (nodes.length > 0) {
                const rightmostNode = nodes.reduce((max, node) => 
                    node.position.x > max.position.x ? node : max, nodes[0]);
                
                position.x = rightmostNode.position.x + 300;
                position.y = rightmostNode.position.y;
            }
            
            // Adicionar ao fluxo
            const response = await api.post(`/flows-new/${id}/questions`, {
                question_bank_id: newQuestionBank.id,
                position_x: position.x,
                position_y: position.y,
            });
            
            console.log('Novo nó adicionado ao fluxo:', response.data);
            
            showNotification(
                questionData.is_final 
                    ? 'Conclusão criada e adicionada com sucesso' 
                    : 'Questão criada e adicionada com sucesso',
                'success'
            );

            // Fechar todos os modais
            closeAllModals();

            // Atualizar o fluxo para mostrar a nova questão/conclusão
            await Promise.all([
                fetchFlow(true),
                fetchQuestionBank()
            ]);
        } catch (error) {
            console.error('Erro ao criar questão/conclusão:', error);
            showNotification(`Erro ao criar ${questionData.is_final ? 'conclusão' : 'questão'}: ${error.message || 'Erro desconhecido'}`, 'error');
            setPreservingViewport(false);
        } finally {
            setSaving(false);
        }
    };

    // Função para recarregar o banco de questões
    const fetchQuestionBank = async () => {
        try {
            console.log('Carregando banco de questões');
            const questions = await questionBankService.list();
            console.log('Banco de questões carregado:', questions);
            
            if (!questions || !Array.isArray(questions)) {
                console.error('Resposta inválida ao carregar banco de questões:', questions);
                throw new Error('Formato de resposta inválido ao carregar banco de questões');
            }
            
            // Garantir que todos os campos necessários estejam presentes
            const processedQuestions = questions.map(q => ({
                ...q,
                options: q.options || [],
                is_final: q.is_final || false,
                is_success: q.is_success || false,
                final_message: q.final_message || '',
                article_reference: q.article_reference || '',
            }));
            
            // Ordenar questões alfabeticamente pelo título
            const sortedQuestions = [...processedQuestions].sort((a, b) => {
                if (!a.title) return 1;
                if (!b.title) return -1;
                return a.title.localeCompare(b.title, 'pt-BR');
            });
            
            console.log('Questões ordenadas alfabeticamente:', sortedQuestions);
            setQuestionBankItems(sortedQuestions);
        } catch (error) {
            console.error('Erro ao carregar banco de questões:', error);
            if (showNotification) {
                showNotification(`Erro ao carregar banco de questões: ${error.message || 'Erro desconhecido'}`, 'error');
            }
            // Definir estado padrão em caso de erro
            setQuestionBankItems([]);
        }
    };
    
    // Adicionar a função onNodeDragStop otimizada
    const onNodeDragStop = useCallback((event, node) => {
        if (!flow || !node || !node.position) return;

        // Debounce para evitar múltiplas atualizações
        if (node.position.x === node.data?.position_x && node.position.y === node.data?.position_y) {
            return; // Evita atualização se a posição não mudou
        }

        // Atualiza apenas o nó que foi movido
        setNodes(prevNodes => prevNodes.map(n => {
            if (n.id === node.id) {
                return {
                    ...n,
                    position: node.position,
                    data: {
                        ...n.data,
                        position_x: node.position.x,
                        position_y: node.position.y
                    }
                };
            }
            return n;
        }));

        // Atualiza o backend de forma otimizada
        if (flow.questions && Array.isArray(flow.questions)) {
            const updatedQuestion = flow.questions.find(q => q.id?.toString() === node.id);
            if (updatedQuestion) {
                const updatedFlow = {
                    ...flow,
                    questions: flow.questions.map(q => {
                        if (q.id?.toString() === node.id) {
                            return {
                                ...q,
                                position_x: node.position.x,
                                position_y: node.position.y
                            };
                        }
                        return q;
                    })
                };

                // Salva silenciosamente no backend com debounce
                const timeoutId = setTimeout(() => {
                    flowService.update(Number(id), updatedFlow).catch(error => {
                        console.error('Erro ao atualizar posição:', error);
                    });
                }, 500); // Delay de 500ms

                return () => clearTimeout(timeoutId);
            }
        }
    }, [flow, id]);
    
    // Função para definir a cor dos nós no MiniMap
    const getNodeColor = (node) => {
        // Conclusões (nós finais)
        if (node.data.is_final) {
            // Verde para conclusões positivas
            if (node.data.is_success) {
                return '#4caf50';  // Verde
            } 
            // Vermelho para conclusões negativas
            else {
                return '#f44336';  // Vermelho
            }
        }
        
        // Questões (nós não finais)
        return '#2196f3';  // Azul
    };

    // Função para lidar com a mudança de seleção
    const onSelectionChange = useCallback(({ nodes, edges }) => {
        // Verificar se há múltiplos nós selecionados
        if (nodes.length > 1) {
            // Priorizar a seleção múltipla
            setMultiSelectedNodes(nodes);
            // Limpar seleções individuais
            setSelectedNode(null);
            setSelectedEdge(null);
            console.log(`Seleção múltipla: ${nodes.length} nós selecionados`);
        } else if (nodes.length === 1) {
            // Seleção de um único nó
            setMultiSelectedNodes([]);
            setSelectedNode(nodes[0]);
            setSelectedEdge(null);
            console.log(`Nó selecionado: ${nodes[0].id}`);
        } else if (edges.length > 0) {
            // Seleção de uma aresta
            setMultiSelectedNodes([]);
            setSelectedNode(null);
            setSelectedEdge(edges[0]);
            console.log(`Aresta selecionada: ${edges[0].id}`);
        } else {
            // Nenhuma seleção
            setMultiSelectedNodes([]);
            setSelectedNode(null);
            setSelectedEdge(null);
            console.log('Nenhuma seleção');
        }
    }, []);

    // Função para clonar múltiplos nós selecionados
    const handleCloneSelectedNodes = useCallback(async () => {
        if (multiSelectedNodes.length <= 1) return;
        
        try {
            preserveCurrentViewport();
            
            console.log('Clonando nós:', multiSelectedNodes);
            
            // Obter os IDs dos nós selecionados
            const nodeIds = multiSelectedNodes.map(node => parseInt(node.id));
            
            // Chamar a API para clonar os nós
            const response = await api.post(`/flows-new/${id}/clone-nodes`, {
                node_ids: nodeIds,
                position_offset: { x: 50, y: 50 }
            });
            
            // Verificar se a resposta contém o mapeamento de IDs
            if (response.data && response.data.id_mapping) {
                console.log('Mapeamento de IDs:', response.data.id_mapping);
                
                // Recarregar o fluxo com os novos nós
                await fetchFlow(true);
                
                // Obter os IDs dos novos nós
                const newNodeIds = Object.values(response.data.id_mapping).map(id => id.toString());
                
                // Selecionar os novos nós
                setNodes(nds => nds.map(node => ({
                    ...node,
                    selected: newNodeIds.includes(node.id.toString())
                })));
                
                // Atualizar o estado de multiSelectedNodes com os novos nós
                const newSelectedNodes = nodes.filter(node => 
                    newNodeIds.includes(node.id.toString())
                );
                
                setMultiSelectedNodes(newSelectedNodes);
            }
            
            showNotification('Nós clonados com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao clonar nós:', error);
            showNotification(`Erro ao clonar nós: ${error.message || 'Erro desconhecido'}`, 'error');
        }
    }, [multiSelectedNodes, id, preserveCurrentViewport, fetchFlow, setNodes, setMultiSelectedNodes, showNotification, nodes]);

    // Definição das funções que estavam faltando
    const setReactFlowInstance = useCallback((instance) => {
        reactFlowInstance.current = instance;
    }, []);
    
    const onDrop = useCallback((event) => {
        event.preventDefault();
        
        if (!reactFlowInstance.current) {
            return;
        }
        
        try {
            // Preservar o viewport atual antes de qualquer operação
            preserveCurrentViewport();
            
            // Verificar se temos dados do menu lateral
            const jsonData = event.dataTransfer.getData('application/json');
            if (jsonData) {
                const questionData = JSON.parse(jsonData);
                console.log('Dados do drag and drop:', questionData);
                
                // Calcular a posição usando screenToFlowPosition (método recomendado)
                const reactFlowBounds = event.currentTarget.getBoundingClientRect();
                const position = reactFlowInstance.current.screenToFlowPosition({
                    x: event.clientX,
                    y: event.clientY,
                });
                
                // Adicionar a questão ao fluxo
                handleAddQuestion(questionData, position);
                return;
            }
            
            // Se não temos dados do menu lateral, verificar se temos uma questão selecionada
            if (selectedQuestion) {
                const position = reactFlowInstance.current.screenToFlowPosition({
                    x: event.clientX,
                    y: event.clientY,
                });
                
                handleAddQuestion(selectedQuestion, position);
                setSelectedQuestion('');
                setOpenDialog(false);
            } else {
                // Se não temos nem dados do menu lateral nem uma questão selecionada,
                // desativar a preservação do viewport
                setPreservingViewport(false);
            }
        } catch (error) {
            console.error('Erro ao processar drop:', error);
            setPreservingViewport(false); // Importante: desativar a preservação em caso de erro
        }
    }, [selectedQuestion, handleAddQuestion, reactFlowInstance, preserveCurrentViewport, setPreservingViewport]);
    
    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);
    
    // Definição dos tipos de arestas
    const edgeTypes = useMemo(() => ({}), []);

    // Função para selecionar todos os nós do mesmo tipo
    const handleSelectSameTypeNodes = useCallback((nodeType) => {
        // nodeType pode ser 'question' ou 'conclusion'
        const isFinal = nodeType === 'conclusion';
        
        // Filtrar nós do mesmo tipo
        const sameTypeNodes = nodes.filter(node => 
            (isFinal && node.data.is_final) || (!isFinal && !node.data.is_final)
        );
        
        if (sameTypeNodes.length > 0) {
            // Marcar os nós como selecionados
            setNodes(nds => nds.map(node => ({
                ...node,
                selected: (isFinal && node.data.is_final) || (!isFinal && !node.data.is_final)
            })));
            
            // Atualizar o estado de multiSelectedNodes
            setMultiSelectedNodes(sameTypeNodes);
            
            // Limpar outras seleções
            setSelectedNode(null);
            setSelectedEdge(null);
            
            console.log(`Selecionados ${sameTypeNodes.length} nós do tipo ${nodeType}`);
        }
    }, [nodes, setNodes, setMultiSelectedNodes]);
    
    // Função para lidar com o clique com botão direito em um nó
    const onNodeContextMenu = useCallback((event, node) => {
        // Prevenir o menu de contexto padrão do navegador
        event.preventDefault();
        
        // Criar um menu de contexto personalizado
        const contextMenu = document.createElement('div');
        contextMenu.id = 'node-context-menu';
        contextMenu.style.position = 'absolute';
        contextMenu.style.left = `${event.clientX}px`;
        contextMenu.style.top = `${event.clientY}px`;
        contextMenu.style.backgroundColor = theme.palette.background.paper;
        contextMenu.style.boxShadow = theme.shadows[4];
        contextMenu.style.borderRadius = '4px';
        contextMenu.style.padding = '8px 0';
        contextMenu.style.zIndex = '2000';
        
        // Opções do menu
        const options = [
            {
                label: 'Selecionar todas as questões',
                onClick: () => handleSelectSameTypeNodes('question'),
                disabled: false
            },
            {
                label: 'Selecionar todas as conclusões',
                onClick: () => handleSelectSameTypeNodes('conclusion'),
                disabled: false
            },
            {
                label: 'Editar',
                onClick: () => handleEditQuestion(node),
                disabled: false
            },
            {
                label: 'Clonar',
                onClick: () => handleCloneQuestion(node),
                disabled: false
            },
            {
                label: 'Excluir',
                onClick: () => handleRemoveQuestion(node.id),
                disabled: false
            }
        ];
        
        // Criar os itens do menu
        options.forEach(option => {
            const item = document.createElement('div');
            item.style.padding = '8px 16px';
            item.style.cursor = option.disabled ? 'default' : 'pointer';
            item.style.opacity = option.disabled ? '0.5' : '1';
            item.style.whiteSpace = 'nowrap';
            item.style.fontSize = '14px';
            
            item.onmouseover = () => {
                if (!option.disabled) {
                    item.style.backgroundColor = alpha(theme.palette.primary.main, 0.1);
                }
            };
            
            item.onmouseout = () => {
                item.style.backgroundColor = 'transparent';
            };
            
            item.onclick = () => {
                if (!option.disabled) {
                    option.onClick();
                    document.body.removeChild(contextMenu);
                }
            };
            
            item.textContent = option.label;
            contextMenu.appendChild(item);
        });
        
        // Adicionar o menu ao corpo do documento
        document.body.appendChild(contextMenu);
        
        // Remover o menu quando clicar fora dele
        const handleClickOutside = (e) => {
            if (!contextMenu.contains(e.target)) {
                document.body.removeChild(contextMenu);
                document.removeEventListener('click', handleClickOutside);
            }
        };
        
        // Adicionar um pequeno atraso para evitar que o menu seja fechado imediatamente
        setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
        }, 100);
        
    }, [theme, handleEditQuestion, handleCloneQuestion, handleRemoveQuestion, handleSelectSameTypeNodes]);

    // Função para remover múltiplos nós de uma vez
    const handleRemoveMultipleQuestions = useCallback(async (nodeIds) => {
        try {
            preserveCurrentViewport();
            
            console.log('Removendo nós:', nodeIds);
            
            // Em vez de tentar excluir todos os nós de uma vez, vamos excluí-los um a um
            let successCount = 0;
            let errorCount = 0;
            
            // Criar uma cópia dos nós atuais para manipulação local
            const currentNodes = [...nodes];
            
            // Excluir cada nó individualmente
            for (const nodeId of nodeIds) {
                try {
                    // Chamar a API para remover o nó
                    await api.delete(`/flows-new/${id}/questions/${nodeId}`);
                    
                    // Remover o nó do estado local
                    const nodeIndex = currentNodes.findIndex(node => node.id === nodeId.toString());
                    if (nodeIndex !== -1) {
                        currentNodes.splice(nodeIndex, 1);
                    }
                    
                    successCount++;
                } catch (error) {
                    console.error(`Erro ao remover nó ${nodeId}:`, error);
                    errorCount++;
                }
            }
            
            // Atualizar o estado com os nós restantes
            setNodes(currentNodes);
            
            // Atualizar as arestas para remover as que estavam conectadas aos nós excluídos
            setEdges(prevEdges => 
                prevEdges.filter(edge => 
                    !nodeIds.includes(parseInt(edge.source)) && 
                    !nodeIds.includes(parseInt(edge.target))
                )
            );
            
            // Limpar a seleção
            setMultiSelectedNodes([]);
            
            // Desativar a preservação do viewport
            setPreservingViewport(false);
            
            // Mostrar notificação de sucesso/erro
            if (successCount > 0) {
                showNotification(`${successCount} nós excluídos com sucesso!`, 'success');
            }
            
            if (errorCount > 0) {
                showNotification(`Não foi possível excluir ${errorCount} nós.`, 'warning');
            }
            
            return { successCount, errorCount };
        } catch (error) {
            console.error('Erro ao remover nós:', error);
            setPreservingViewport(false); // Desativar a preservação em caso de erro
            showNotification(`Erro ao remover nós: ${error.message || 'Erro desconhecido'}`, 'error');
            throw error;
        }
    }, [id, nodes, setNodes, setEdges, preserveCurrentViewport, setMultiSelectedNodes, setPreservingViewport, showNotification]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }
    
    return (
        <Container maxWidth={false} sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 2 }}>
            {/* Substituindo o Paper por um Box simples */}
            <Box sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500 }}>
                        {flow.name || (id === 'new' ? 'Novo Fluxo' : 'Editar Fluxo')}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                        
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                        >
                            Voltar
                        </Button>
                    </Box>
                </Box>
                
                {id === 'new' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Salve o fluxo antes de adicionar questões.
                    </Alert>
                )}
            </Box>
            
            <Paper sx={{ 
                flexGrow: 1, 
                position: 'relative', 
                display: 'flex', 
                overflow: 'hidden', 
                height: '100%' 
            }}>
                {/* Menu lateral */}
                <SidePanel
                    isMenuExpanded={isMenuExpanded}
                    handleToggleExpand={handleToggleExpand}
                    expandedSections={expandedSections}
                    handleSectionToggle={handleSectionToggle}
                    includedQuestions={uniqueIncludedQuestions}
                    availableQuestions={availableQuestions}
                    conclusions={conclusions}
                    renderQuestionCard={renderQuestionCard}
                    theme={theme}
                />
                
                <Box 
                    sx={{ 
                        flex: 1,
                        height: '100%',
                        position: 'relative',
                        ml: isMenuExpanded ? '320px' : 0, // Adiciona margem quando o menu está expandido
                        transition: 'margin-left 0.3s ease',
                        width: isMenuExpanded ? 'calc(100% - 320px)' : '100%', // Ajusta a largura baseado no menu
                    }}
                >
                    {/* Botão para expandir/recolher o menu */}
                    <Tooltip title={isMenuExpanded ? "Recolher menu" : "Expandir menu"} placement="right">
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 10,
                                left: 10, // Sempre mantém o botão na mesma posição
                                zIndex: 9999,
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {!isMenuExpanded && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.background.paper, 0.5),
                                        backdropFilter: 'blur(5px)',
                                        zIndex: -1,
                                    }}
                                />
                            )}
                            <IconButton
                                onClick={handleToggleExpand}
                                sx={{
                                    backgroundColor: isMenuExpanded ? theme.palette.background.paper : theme.palette.primary.main,
                                    color: isMenuExpanded ? 'inherit' : 'white',
                                    border: `1px solid ${isMenuExpanded ? theme.palette.divider : theme.palette.primary.dark}`,
                                    '&:hover': {
                                        backgroundColor: isMenuExpanded 
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : theme.palette.primary.dark,
                                        transform: 'scale(1.1)',
                                    },
                                    width: 48,
                                    height: 48,
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`,
                                    transition: 'all 0.3s ease',
                                    animation: !isMenuExpanded ? 'pulse 2s infinite' : 'none',
                                    '@keyframes pulse': {
                                        '0%': {
                                            boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.7)}`,
                                        },
                                        '70%': {
                                            boxShadow: `0 0 0 10px ${alpha(theme.palette.primary.main, 0)}`,
                                        },
                                        '100%': {
                                            boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}`,
                                        },
                                    },
                                }}
                            >
                                {isMenuExpanded ? <ChevronLeftIcon fontSize="medium" /> : <ChevronRightIcon fontSize="medium" />}
                            </IconButton>
                        </Box>
                    </Tooltip>
                    
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onInit={setReactFlowInstance}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onNodeClick={onNodeClick}
                        onEdgeClick={onEdgeClick}
                        onPaneClick={onPaneClick}
                        onSelectionChange={onSelectionChange}
                        onNodeContextMenu={onNodeContextMenu}
                        fitView
                        fitViewOptions={{ padding: 0.2 }}
                        minZoom={0.1}
                        maxZoom={2}
                        defaultEdgeOptions={{
                            type: 'smoothstep'
                        }}
                    >
                        <ZoomSlider />
                        <Controls />
                        <Background
                            variant="cross"
                            gap={20}
                            size={1}
                            color={theme.palette.mode === 'dark' 
                                ? alpha(theme.palette.divider, 0.2)
                                : alpha(theme.palette.divider, 0.15)
                            }
                            style={{
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.default, 0.9)
                                    : alpha(theme.palette.background.default, 0.95)
                            }}
                        />
                        <MiniMap
                            style={{
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? alpha(theme.palette.background.paper, 0.9)
                                    : theme.palette.background.paper
                            }}
                            zoomable
                            pannable
                            nodeColor={getNodeColor}
                            nodeStrokeWidth={3}
                            maskColor={alpha(theme.palette.background.default, 0.5)}
                        />
                        
                        {/* Botão de clonagem para múltiplos nós selecionados */}
                        {multiSelectedNodes.length > 1 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    zIndex: 999999,
                                    display: 'flex',
                                    gap: 1,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                    padding: 1,
                                    borderRadius: 2,
                                    boxShadow: theme.shadows[4],
                                }}
                            >
                                <Tooltip title={`Clonar ${multiSelectedNodes.length} nós selecionados`}>
                                    <IconButton
                                        size="small"
                                        color="info"
                                        onClick={handleCloneSelectedNodes}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.info.main, 0.2)
                                            }
                                        }}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={`Excluir ${multiSelectedNodes.length} nós selecionados`}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            // Confirmar antes de excluir múltiplos nós
                                            if (window.confirm(`Tem certeza que deseja excluir ${multiSelectedNodes.length} nós selecionados?`)) {
                                                // Obter os IDs dos nós selecionados
                                                const nodeIds = multiSelectedNodes.map(node => parseInt(node.id));
                                                
                                                // Excluir os nós um a um
                                                handleRemoveMultipleQuestions(nodeIds)
                                                    .catch(error => {
                                                        console.error('Erro ao excluir nós:', error);
                                                    });
                                            }
                                        }}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.2)
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Cancelar seleção">
                                    <IconButton
                                        size="small"
                                        color="secondary"
                                        onClick={() => {
                                            // Limpar a seleção múltipla
                                            setMultiSelectedNodes([]);
                                            // Atualizar os nós para remover a seleção visual
                                            setNodes(nds => nds.map(node => ({
                                                ...node,
                                                selected: false
                                            })));
                                        }}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.secondary.main, 0.2)
                                            }
                                        }}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* SpeedDial para adicionar questões */}
                        <SpeedDial
                            ariaLabel="Adicionar questão"
                            sx={{
                                position: 'absolute',
                                bottom: 180,
                                right: 16,
                            }}
                            icon={<SpeedDialIcon />}
                            onClose={() => setSpeedDialOpen(false)}
                            onOpen={() => setSpeedDialOpen(true)}
                            open={speedDialOpen}
                            direction="up"
                            FabProps={{
                                sx: {
                                    bgcolor: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    }
                                }
                            }}
                        >
                            <SpeedDialAction
                                key="new-question"
                                icon={<QuestionAnswerIcon />}
                                tooltipTitle="Nova Questão"
                                onClick={() => handleOpenCreateModal('question')}
                                FabProps={{
                                    disabled: id === 'new'
                                }}
                            />
                            <SpeedDialAction
                                key="new-conclusion"
                                icon={<AssignmentIcon />}
                                tooltipTitle="Nova Conclusão"
                                onClick={() => handleOpenCreateModal('conclusion')}
                                FabProps={{
                                    disabled: id === 'new'
                                }}
                            />
                        </SpeedDial>


                        {/* Botões para manipular o nó selecionado - só aparecem se não houver múltiplos nós selecionados */}
                        {selectedNode && multiSelectedNodes.length <= 1 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    zIndex: 999999,
                                    display: 'flex',
                                    gap: 1,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                    padding: 1,
                                    borderRadius: 2,
                                    boxShadow: theme.shadows[4],
                                }}
                            >
                                {!selectedNode.data.is_final && (
                                    <Tooltip title="Definir como Primeira Questão">
                                        <IconButton
                                            size="small"
                                            color="success"
                                            onClick={() => handleSetAsFirstQuestion(selectedNode.id)}
                                            sx={{
                                                backgroundColor: selectedNode.data.isFirstQuestion 
                                                    ? alpha(theme.palette.success.main, 0.1)
                                                    : 'transparent',
                                                border: selectedNode.data.isFirstQuestion 
                                                    ? `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                                    : 'none',
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.success.main, 0.2)
                                                }
                                            }}
                                        >
                                            <PlayCircleOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Tooltip title="Editar">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleEditQuestion(selectedNode)}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.2)
                                            }
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Clonar">
                                    <IconButton
                                        size="small"
                                        color="info"
                                        onClick={() => handleCloneQuestion(selectedNode)}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.info.main, 0.2)
                                            }
                                        }}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Excluir">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveQuestion(selectedNode.id)}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.2)
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Botão para excluir aresta selecionada - só aparece se não houver múltiplos nós selecionados */}
                        {selectedEdge && multiSelectedNodes.length <= 1 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    zIndex: 10,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                    padding: 1,
                                    borderRadius: 1,
                                    boxShadow: theme.shadows[3],
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Conexão Selecionada
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="Excluir Conexão">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleRemoveEdge(selectedEdge.id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Fechar">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSelectedEdge(null)}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        )}
                    </ReactFlow>
                </Box>
            </Paper>
            
            {/* Diálogo para adicionar questão */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>Adicionar Questão do Banco</DialogTitle>
                <DialogContent>
                    {!flow.id ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            É necessário salvar o fluxo antes de adicionar questões.
                        </Alert>
                    ) : null}
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="question-select-label">Selecione uma Questão</InputLabel>
                        <Select
                            labelId="question-select-label"
                            id="question-select"
                            value={selectedQuestion}
                            onChange={(e) => setSelectedQuestion(e.target.value)}
                            label="Selecione uma Questão"
                        >
                            {questionBankItems.map((question) => (
                                <MenuItem key={question.id} value={question.id}>
                                    {question.text}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancelar
                    </Button>
                    {!flow.id ? (
                        <Button 
                            onClick={handleSave} 
                            color="primary" 
                            variant="contained"
                            disabled={saving}
                        >
                            {saving ? 'Salvando...' : 'Salvar Fluxo'}
                        </Button>
                    ) : (
                        <Button 
                            onClick={() => handleAddQuestion(questionBankItems.find(q => q.id === parseInt(selectedQuestion)), { x: 100, y: 100 })} 
                            color="primary" 
                            disabled={!selectedQuestion || saving}
                        >
                            {saving ? 'Adicionando...' : 'Adicionar'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Modal de criação de questão/conclusão */}
            {openCreateModal && (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: 9999999 }}
                    className="modal-wrapper"
                >
                    {createModalType === 'question' ? (
                        <QuestionForm
                            open={true}
                            onClose={handleCloseCreateModal}
                            onSave={(questionData) => {
                                handleSaveNewQuestion(questionData);
                                // Fechar todos os modais após salvar
                                closeAllModals();
                            }}
                            isNew={true}
                        />
                    ) : (
                        <ConclusionForm
                            open={true}
                            onClose={handleCloseCreateModal}
                            onSave={(questionData) => {
                                handleSaveNewQuestion(questionData);
                                // Fechar todos os modais após salvar
                                closeAllModals();
                            }}
                            isNew={true}
                            useExternalDialog={false}
                        />
                    )}
                </div>
            )}

            {/* Modal de edição de questão */}
            {isQuestionFormOpen && selectedNodeForEdit && (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: 9999999 }}
                    className="modal-wrapper"
                >
                    <QuestionForm
                        open={true}
                        onClose={() => {
                            console.log("Fechando modal de edição de questão");
                            closeAllModals();
                        }}
                        onSave={async (questionData) => {
                            try {
                                // Atualizar a questão no banco
                                const response = await api.put(`/question-bank/${questionData.id}`, questionData);
                                
                                // Atualizar o nó no fluxo visual
                                setNodes(prevNodes => 
                                    prevNodes.map(node => {
                                        if (node.id === selectedNodeForEdit.id) {
                                            return {
                                                ...node,
                                                data: {
                                                    ...node.data,
                                                    ...questionData
                                                }
                                            };
                                        }
                                        return node;
                                    })
                                );
                                
                                showNotification('Questão atualizada com sucesso!', 'success');
                                closeAllModals();
                            } catch (error) {
                                console.error('Erro ao salvar questão:', error);
                                showNotification(`Erro ao salvar questão: ${error.message || 'Erro desconhecido'}`, 'error');
                            }
                        }}
                        question={{
                            id: selectedNodeForEdit.data.id,
                            text: selectedNodeForEdit.data.text || '',
                            options: selectedNodeForEdit.data.options || [],
                            is_final: selectedNodeForEdit.data.is_final === true,
                            article_reference: selectedNodeForEdit.data.article_reference || '',
                            dynamic_text: selectedNodeForEdit.data.dynamic_text || {},
                            previous_question: selectedNodeForEdit.data.previous_question || null,
                            next_question_logic: selectedNodeForEdit.data.next_question_logic || {},
                            final_message: selectedNodeForEdit.data.final_message || '',
                            is_success: selectedNodeForEdit.data.is_success
                        }}
                        isNew={false}
                    />
                </div>
            )}

            {/* Modal de edição de conclusão */}
            {isConclusionFormOpen && selectedNodeForEdit && (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: 9999999 }}
                    className="modal-wrapper"
                >
                    <ConclusionForm
                        open={true}
                        onClose={() => {
                            console.log("Fechando modal de edição de conclusão");
                            closeAllModals();
                        }}
                        onSave={async (questionData) => {
                            try {
                                console.log("Salvando conclusão:", questionData);
                                // Atualizar a questão no banco
                                const response = await api.put(`/question-bank/${questionData.id}`, questionData);
                                
                                // Atualizar o nó no fluxo visual
                                setNodes(prevNodes => 
                                    prevNodes.map(node => {
                                        if (node.id === selectedNodeForEdit.id) {
                                            return {
                                                ...node,
                                                data: {
                                                    ...node.data,
                                                    ...questionData
                                                }
                                            };
                                        }
                                        return node;
                                    })
                                );
                                
                                showNotification('Conclusão atualizada com sucesso!', 'success');
                                closeAllModals();
                            } catch (error) {
                                console.error('Erro ao salvar conclusão:', error);
                                showNotification(`Erro ao salvar conclusão: ${error.message || 'Erro desconhecido'}`, 'error');
                            }
                        }}
                        question={{
                            id: selectedNodeForEdit.data.id,
                            text: selectedNodeForEdit.data.text || '',
                            options: selectedNodeForEdit.data.options || [],
                            is_final: true,
                            article_reference: selectedNodeForEdit.data.article_reference || '',
                            dynamic_text: selectedNodeForEdit.data.dynamic_text || {},
                            previous_question: selectedNodeForEdit.data.previous_question || null,
                            final_message: selectedNodeForEdit.data.final_message || '',
                            is_success: selectedNodeForEdit.data.is_success
                        }}
                        isNew={false}
                        useExternalDialog={false}
                    />
                </div>
            )}
        </Container>
    );
};

// Componente principal envolvido com ReactFlowProvider
export const FlowNew = () => {
    return (
        <ReactFlowProvider>
            <FlowEditor />
        </ReactFlowProvider>
    );
}; 